import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Modal, ModalHeader, ModalBody } from '@evertrue/et-components';
import EverTrue from 'app';
import SessionStore from 'base/session/session-store';
import Api from 'entities/helpers/api';
import CadenceBuilderStep from './cadence-builder-step';
import CadenceReviewStep from './cadence-review-step';
import CadenceSetupStep from './cadence-setup-step';
import useCadenceMutations from '../../hooks/use-cadence-mutations';
import useCadence from '../../hooks/use-cadence';
import { useEmailTemplatesProvider } from 'apps/outreach/email/templates/email-templates-context';
import { useEmailSignaturesProvider } from 'apps/outreach/email/signatures/email-signatures-context';
import TemplatePickerModal from '../email-template/template-picker-modal';
import SignaturePickerModal from '../email-template/signature-picker-modal';
import useTemplates from '../../hooks/use-templates';
import cadenceGqlCall from '../../utils/cadence-gql-call';
import {
  ORG_TAGS_QUERY,
  REGISTER_TAG_MUTATION,
  CREATE_CADENCE_WITH_CONFIG_MUTATION,
  UPDATE_CADENCE_WITH_CONFIG_MUTATION,
  UPDATE_CADENCE_MUTATION,
} from '../../utils/cadence-queries';
import {
  STEP_TYPE_LABELS as SHARED_STEP_TYPE_LABELS,
  collectEmailSteps,
  getIncompleteEmailSteps,
  getOverlongTaskSteps,
  TASK_STEP_NAME_MAX_LENGTH,
  TASK_STEP_DESCRIPTION_MAX_LENGTH,
} from '../../utils/cadence-step-utils';
import { findIncompleteConfigs } from '../../utils/config-completion';
import DuplicateCadenceModal from '../duplicate/duplicate-cadence-modal';
import useInteractionTypes from '../../hooks/use-interaction-types';
import usePortfolioTeams from '../../hooks/use-portfolio-teams';
import useCadences from '../../hooks/use-cadences';
import useLists from '../../hooks/use-lists';
import useSavedSearches from '../../hooks/use-saved-searches';
import useProposals from '../../hooks/use-proposals';
import useProposalLabels from '../../hooks/use-proposal-labels';
import useDebounce from 'hooks/use-debounce';
import { SELECTION_METHOD_OPTIONS } from '../../utils/cadence-constants';
import { fetchUsersById } from 'apps/volunteers/vms-queries';
import useDomainStatus from '../../hooks/use-domain-status';

/** Helper to normalize removal config from backend snake_case to UI camelCase. */
const normalizeRemovalConfig = (type, raw) => {
  const cfg = raw || {};
  switch (type) {
    case 'portfolio_stage_reached':
      return {
        teamId: cfg.team_id || cfg.teamId || null,
        teamName: cfg.team_name || cfg.teamName || null,
        stageId: cfg.stage_id || cfg.stageId || null,
        stageName: cfg.stage_name || cfg.stageName || null,
      };
    case 'cadence_qualification':
      return {
        cadenceId: cfg.cadence_id || cfg.cadenceId || null,
        cadenceName: cfg.cadence_name || cfg.cadenceName || null,
      };
    case 'saved_search':
      return {
        savedSearchId: cfg.saved_search_tag_id || cfg.saved_search_id || cfg.savedSearchId || null,
        savedSearchName: cfg.saved_search_name || cfg.savedSearchName || null,
        savedSearchListId: cfg.saved_search_list_id ?? cfg.savedSearchListId ?? null,
      };
    case 'list_membership':
      return {
        listId: cfg.list_id || cfg.listId || null,
        listName: cfg.list_name || cfg.listName || null,
      };
    default:
      return cfg;
  }
};

/** Helper to convert UI camelCase config to backend snake_case for mutations.
 *
 * Only writes keys the BE evaluator actually reads. Display-only fields
 * (team_name, cadence_name, list_name, saved_search_name) are not stored —
 * the FE renames are looked up from the picker on render instead.
 */
const buildRemovalConfig = (condition) => {
  const { type, config } = condition;
  switch (type) {
    case 'portfolio_stage_reached':
      return { team_id: config.teamId, stage_id: config.stageId, stage_name: config.stageName };
    case 'cadence_qualification':
      return { cadence_id: config.cadenceId, cadence_name: config.cadenceName || null };
    case 'saved_search': {
      // SavedSearchEvaluator.create_from_removal_rule reads saved_search_tag_id.
      const out = { saved_search_tag_id: config.savedSearchId, saved_search_name: config.savedSearchName || null };
      if (config.savedSearchListId != null) out.saved_search_list_id = Number(config.savedSearchListId);
      return out;
    }
    case 'list_membership':
      return { list_id: config.listId };
    default:
      return {};
  }
};

/** Normalize legacy metric/removal type keys from existing cadences to current backend keys. */
const LEGACY_KEY_MAP = {
  email_open_rate: 'email_opened',
  email_click_rate: 'email_clicked',
  email_response_rate: 'email_replied',
  bounced: 'email_bounced',
};
const normalizeKey = (key) => {
  const lower = (key || '').toLowerCase();
  return LEGACY_KEY_MAP[lower] || lower;
};

/** Split the comma-separated reply-to input into a trimmed address list, or undefined when empty. */
export const parseReplyToEmails = (value) =>
  value
    ? value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : undefined;

/**
 * Resolve the reply-to field to restore into the setup form: prefer the cadence-level
 * `replyToEmails` list (new), then the first automated email step's `reply_to_emails`
 * (records saved before cadence-level sender config existed), then its deprecated
 * singular `reply_to_email` (older records saved before multi-address support).
 */
export const resolveReplyToEmails = (existingCadence, eventConfig) =>
  (Array.isArray(existingCadence?.replyToEmails) ? existingCadence.replyToEmails.join(', ') : null) ||
  (Array.isArray(eventConfig?.reply_to_emails) ? eventConfig.reply_to_emails.join(', ') : null) ||
  eventConfig?.reply_to_email ||
  null;

/** Step type labels (from shared utility with creation-flow overrides). */
const STEP_TYPE_LABELS = {
  ...SHARED_STEP_TYPE_LABELS,
  email: 'Automated Email',
  task: 'Manual Task',
  exit: 'Exit Cadence',
};

/**
 * Create Cadence page - 3-step stepper layout.
 * Step 1: Configure Cadence, Step 2: Cadence Steps builder, Step 3: Review & Publish.
 */
const CreateCadencePage = ({
  cadenceType: cadenceTypeProp = 'cadence',
  cadenceId = null,
  shellId = null,
  readonly: readonlyProp = false,
}) => {
  const isNewBlueprint = cadenceTypeProp === 'blueprint';
  const cadenceTypeFromProp = isNewBlueprint ? 'cadence' : cadenceTypeProp.replace(/-/g, '_');
  // When editing, derive cadenceType from the loaded cadence (the edit route
  // doesn't pass cadenceType, so the prop defaults to 'cadence' even for prospect plans)
  const [resolvedCadenceType, setResolvedCadenceType] = useState(cadenceTypeFromProp);
  const cadenceType = resolvedCadenceType;
  const isProspectPlan = cadenceType === 'prospect_plan';
  const isDebugMode = new URLSearchParams(window.location.search).get('debug') === 'true';

  const {
    enrollMember,
    bulkEnrollMembers,
    createCadenceListAudience,
    createCadenceAudience,
    saveIndividualCadenceAudiences,
    deleteCadenceAudience,
    updateCadenceSenderConfig,
    updateCadenceEvent,
    saveCadenceAsTemplate,
    activateCadence,
    fetchCadenceSharingSettings,
    createCadenceFromTemplate,
  } = useCadenceMutations();
  const [savedCadenceId, setSavedCadenceId] = useState(null);
  const effectiveCadenceId = cadenceId || savedCadenceId;
  const {
    cadence: existingCadence,
    loading: existingLoading,
    refetch: refetchCadence,
  } = useCadence(effectiveCadenceId);
  // Auto-force readonly for VIEW users (no canEdit) opening an existing cadence via direct URL
  const readonly =
    readonlyProp || (!!effectiveCadenceId && !existingLoading && existingCadence != null && !existingCadence.canEdit);
  const { status: domainStatus, domain: orgDomainObj } = useDomainStatus();
  const isOwner = !!(EverTrue.store?.user?.isSuperUser?.() || EverTrue.store?.user?.isOwner?.());
  const latestVersionRef = useRef(null);
  const getVersion = () => latestVersionRef.current ?? existingCadence?.version;
  const trackVersion = (v) => {
    if (v != null) latestVersionRef.current = v;
  };
  const { templates: outreachTemplates } = useEmailTemplatesProvider();
  const { signatures } = useEmailSignaturesProvider();
  const { templates: cadenceShells } = useTemplates();
  const contactLists = useLists();
  const savedSearches = useSavedSearches();
  // Search-as-you-type against the backend (NEXDEV-2120) — a fixed unfiltered
  // browse window can never cover every org's proposals, so typing must
  // query the server rather than only filtering what's already loaded.
  const [proposalSearch, setProposalSearch] = useState('');
  const debouncedProposalSearch = useDebounce(proposalSearch, 300);
  const proposals = useProposals(debouncedProposalSearch);
  const { interactionTypes } = useInteractionTypes();
  const portfolioTeams = usePortfolioTeams();
  const { cadences: allCadences } = useCadences();
  const cadenceOptions = (allCadences || [])
    .filter(
      (c) =>
        !c.isTemplate &&
        (c.cadenceType || '').toLowerCase() === 'cadence' &&
        (c.displayStatusLabel || '').toLowerCase() !== 'archived'
    )
    .map((c) => ({ label: c.name, value: String(c.id) }));
  const defaultName = isNewBlueprint
    ? 'Untitled Blueprint'
    : isProspectPlan
      ? 'Untitled Prospect Plan'
      : 'Untitled Cadence';
  // Derive typeLabel from both the prop (new blueprints) and server data (editing existing templates)
  const isBlueprint = isNewBlueprint || existingCadence?.isTemplate;
  const typeLabel = isBlueprint ? 'Blueprint' : isProspectPlan ? 'Prospect Plan' : 'Cadence';
  const flowLocked =
    !isBlueprint &&
    (cadenceType === 'cadence' || cadenceType === 'prospect_plan') &&
    (existingCadence?.status === 'active' || existingCadence?.status === 'paused');
  const [currentStep, setCurrentStep] = useState(1);
  const [cadenceName, setCadenceName] = useState(defaultName);
  const [isEditingName, setIsEditingName] = useState(false);
  const [steps, setSteps] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false); // Synchronous guard against double-save race conditions
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const dirtyEnabledRef = useRef(false);
  // Set to true before an async-init state update to prevent it from triggering isDirty.
  // The dirty effect resets it to false after consuming it once.
  const suppressNextDirtyRef = useRef(false);
  const prevDirtyValsRef = useRef(null);
  const [showBlueprintModal, setShowBlueprintModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [namePromptOpen, setNamePromptOpen] = useState(false);
  const [namePromptValue, setNamePromptValue] = useState('');
  const [useNewFromTemplateOpen, setUseNewFromTemplateOpen] = useState(false);
  const [useNewFromTemplateName, setUseNewFromTemplateName] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [prePopulated, setPrePopulated] = useState(false);
  const [drawerTab, setDrawerTab] = useState('details');
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);

  // Step 1: Recipients
  const [isRecipientsOpen, setIsRecipientsOpen] = useState(false);
  const [selectionMethod, setSelectionMethod] = useState(SELECTION_METHOD_OPTIONS[0]);
  const [selectedList, setSelectedList] = useState(null);
  const [selectedSavedSearch, setSelectedSavedSearch] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);

  // Step 1: Success Metric (single in normal mode, multiple in debug mode)
  const [successMetric, setSuccessMetric] = useState(null);
  const [additionalMetrics, setAdditionalMetrics] = useState([]);

  // Step 1: Advanced Settings
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  // Collapsible subsections within Advanced Settings
  const [isExitConditionsOpen, setIsExitConditionsOpen] = useState(false);
  const [isSenderDetailsOpen, setIsSenderDetailsOpen] = useState(false);
  const [isSharingOpen, setIsSharingOpen] = useState(false);
  const [isProposalsOpen, setIsProposalsOpen] = useState(false);
  const [isBlueprintOpen, setIsBlueprintOpen] = useState(false);
  const [isTagsOpen, setIsTagsOpen] = useState(false);
  const [isEnrollmentLimitOpen, setIsEnrollmentLimitOpen] = useState(false);
  const [singleEnrollmentOnly, setSingleEnrollmentOnly] = useState(existingCadence?.singleEnrollmentOnly ?? false);
  const [enrollmentLocked, setEnrollmentLocked] = useState(existingCadence?.enrollmentLocked ?? false);
  // Sender Details fields — use the org's verified sending domain, not the user's personal email domain.
  const orgDomain = orgDomainObj?.name || 'example.com';
  const [senderEmailPrefix, setSenderEmailPrefix] = useState(() => {
    const u = SessionStore.getUser();
    const fromParts = [u?.first_name, u?.last_name]
      .filter(Boolean)
      .join('.')
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, '');
    if (fromParts) return fromParts;
    const nameParts = (u?.name || '').trim().split(/\s+/);
    return nameParts
      .slice(0, 2)
      .filter(Boolean)
      .join('.')
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, '');
  });
  const [senderName, setSenderName] = useState(() => {
    const raw = SessionStore.getUser()?.name || '';
    return raw.replace(/(?:^|[\s.])(\w)/g, (m, c) => m.replace(c, c.toUpperCase()));
  });
  const [replyToEmail, setReplyToEmail] = useState(() => {
    const user = SessionStore.getUser();
    return user?.email || '';
  });
  const [sharedUsers, setSharedUsers] = useState([]); // [{userId, permission: 'view'|'edit', label, shareId?}]
  const [, setOriginalSharedUsers] = useState([]); // shares loaded from backend (for revocation)
  const [pendingShareUsers, setPendingShareUsers] = useState([]); // users selected in picker before adding
  const [pendingSharePermission, setPendingSharePermission] = useState('view'); // permission for pending add
  // Auto-removal rules / failure events (multiple)
  const [removalRules, setRemovalRules] = useState([]);
  const [linkedProposals, setLinkedProposals] = useState([]);
  const [cadenceTemplate, setCadenceTemplate] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const [campaignTags, setCampaignTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);

  /**
   * Reverse-map a backend eventConfig to the UI condition object
   * used by AddConditionModal / ConditionStepCard.
   */
  const restoreConditionFromConfig = (eventConfig) => {
    if (!eventConfig || !eventConfig.condition_type) return null;

    const condType = eventConfig.condition_type;

    // New direct condition types
    switch (condType) {
      case 'email_opened':
        return { type: 'email_opened', label: 'Email Opened?' };
      case 'email_replied':
        return { type: 'email_replied', label: 'Responded to Email?' };
      case 'email_clicked':
        return { type: 'email_clicked', label: 'Email Link Clicked?' };
      case 'had_interaction':
      case 'interaction_created':
        return { type: 'interaction_created', label: 'Had Interaction', interactionType: eventConfig.interaction_type };
      case 'made_gift':
      case 'gift_given':
        return {
          type: 'gift_given',
          label: 'Made Gift',
          minAmount: eventConfig.min_amount,
          designation: eventConfig.designation,
        };
      case 'portfolio_stage':
      case 'portfolio_stage_reached':
        return {
          type: 'portfolio_stage_reached',
          label: 'Moved into Portfolio Stage',
          portfolioTeamId: eventConfig.portfolio_team_id || null,
          portfolioTeamName: eventConfig.portfolio_team_name || null,
          portfolioStage: eventConfig.portfolio_stage || eventConfig.value,
        };
      case 'saved_search':
        return {
          type: 'saved_search',
          label: 'Qualifies for Saved Search',
          // Backend persists saved_search_tag_id; saved_search_id was a legacy
          // attribute_check key. Read both so the picker pre-selects on edit.
          savedSearch: eventConfig.saved_search_tag_id || eventConfig.saved_search_id || eventConfig.value,
          savedSearchName: eventConfig.saved_search_name || null,
          savedSearchListId: eventConfig.saved_search_list_id ?? null,
        };
      case 'in_list':
      case 'list_membership':
        return {
          type: 'list_membership',
          label: 'Is in List',
          list: eventConfig.list_id || eventConfig.value,
          listName: eventConfig.list_name || null,
        };
      default:
        break;
    }

    // Legacy has_engagement → map to specific type
    if (condType === 'has_engagement') {
      const engTypes = eventConfig.engagement_types || [];
      if (engTypes.includes('email_opened')) return { type: 'email_opened', label: 'Email Opened?' };
      if (engTypes.includes('email_replied')) return { type: 'email_replied', label: 'Responded to Email?' };
      if (engTypes.includes('email_clicked')) return { type: 'email_clicked', label: 'Email Link Clicked?' };
      if (engTypes.includes('interaction_created'))
        return { type: 'interaction_created', label: 'Had Interaction', interactionType: eventConfig.interaction_type };
      if (engTypes.includes('gift_given'))
        return {
          type: 'gift_given',
          label: 'Made Gift',
          minAmount: eventConfig.min_amount,
          designation: eventConfig.designation,
        };
      return { type: 'email_opened', label: 'Email Opened?' };
    }

    // Legacy attribute_check → map to specific type
    if (condType === 'attribute_check') {
      if (eventConfig.attribute === 'portfolio_stage')
        return {
          type: 'portfolio_stage_reached',
          label: 'Moved into Portfolio Stage',
          portfolioStage: eventConfig.value,
        };
      if (eventConfig.attribute === 'saved_search')
        return {
          type: 'saved_search',
          label: 'Qualifies for Saved Search',
          savedSearch: eventConfig.value,
          savedSearchName: eventConfig.saved_search_name || null,
          savedSearchListId: eventConfig.saved_search_list_id ?? null,
        };
      if (eventConfig.attribute === 'in_list')
        return { type: 'list_membership', label: 'Is in List', list: eventConfig.value };
      return { type: 'portfolio_stage_reached', label: 'Attribute Check' };
    }

    return null;
  };

  // Pre-populate from existing cadence (review mode)
  useEffect(() => {
    if (!existingCadence || prePopulated) return;
    // Override cadenceType from the loaded cadence so prospect plans get the
    // correct single-contact UI even when the edit route doesn't pass cadenceType
    if (existingCadence.cadenceType) {
      const ct = (existingCadence.cadenceType || '').toLowerCase().replace(/-/g, '_');
      if (ct && ct !== resolvedCadenceType) setResolvedCadenceType(ct);
    }
    latestVersionRef.current = existingCadence.version;
    setCadenceName(existingCadence.name || defaultName);
    // Build a lookup of all events by ID for branch reconstruction
    const allEvents = existingCadence.events || [];
    const edges = existingCadence.edges || [];
    const eventById = {};
    allEvents.forEach((e) => {
      eventById[String(e.id)] = e;
    });

    // Helper to map a backend event to a UI step object
    const eventToStep = (e) => {
      const evtType = e.eventType.toLowerCase();
      // Backend 'task' events → UI 'task' type with taskType derived from action_type
      // Backend 'email' with isAutomated=false → also UI 'task' (legacy manual email format)
      const isBackendTask = evtType === 'task';
      const isLegacyManualEmail = evtType === 'email' && e.isAutomated === false;
      const isTask = isBackendTask || isLegacyManualEmail;
      const actionType = e.eventConfig?.action_type || 'send_email';
      // Map backend action_type to UI taskType id
      const taskTypeId = actionType === 'send_email' ? 'email' : actionType;
      return {
        id: e.id,
        referenceId: e.referenceId || null,
        type: isTask ? 'task' : evtType,
        taskType: isTask ? taskTypeId : undefined,
        name: e.eventConfig?.name || '',
        description: e.eventConfig?.description || '',
        subjectOverride: e.eventConfig?.subject || '',
        emailBody: e.eventConfig?.html_body || e.eventConfig?.text_body || '',
        attachments: e.eventConfig?.attachments || [],
        waitType: e.eventConfig?.wait_type || 'duration',
        waitDuration: (() => {
          const days = e.eventConfig?.duration_days;
          if (e.eventConfig?.duration_minutes) return e.eventConfig.duration_minutes;
          if (e.eventConfig?.duration_hours) return e.eventConfig.duration_hours;
          if (days && days >= 7 && days % 7 === 0) return days / 7;
          return days || 1;
        })(),
        waitUnit: (() => {
          if (e.eventConfig?.duration_minutes) return 'minutes';
          if (e.eventConfig?.duration_hours) return 'hours';
          const days = e.eventConfig?.duration_days;
          if (days && days >= 7 && days % 7 === 0) return 'weeks';
          return 'days';
        })(),
        waitUntil: e.eventConfig?.wait_until || '',
        conditionType: e.eventConfig?.condition_type || 'always_true',
        condition: restoreConditionFromConfig(e.eventConfig),
        template: e.eventConfig?.email_template_id
          ? { id: e.eventConfig.email_template_id, name: e.eventConfig.template_name || 'Template' }
          : null,
        signature: e.eventConfig?.signature_id
          ? { id: e.eventConfig.signature_id, signature_name: e.eventConfig.signature_name || 'Signature' }
          : null,
        skipBusinessDayCheck: e.eventConfig?.skip_business_day_check || false,
      };
    };

    // Follow an edge chain from a starting event ID via "next" edges, stopping at mergePointId
    const followEdgeChain = (startEventId, mergePointId) => {
      const chain = [];
      let currentId = startEventId;
      const visited = new Set();
      while (currentId && !visited.has(currentId)) {
        if (mergePointId && String(currentId) === String(mergePointId)) break;
        visited.add(currentId);
        const evt = eventById[String(currentId)];
        if (!evt || evt.eventType.toLowerCase() === 'end') break;
        chain.push(eventToStep(evt));
        const nextEdge = edges.find(
          (ed) => String(ed.fromCadenceEventId) === String(currentId) && ed.edgeType?.toLowerCase() === 'next'
        );
        currentId = nextEdge ? nextEdge.toCadenceEventId : null;
      }
      return chain;
    };

    // Collect IDs of events that are branch children, stopping at merge point
    const branchEventIds = new Set();
    const collectBranchIds = (startId, mergePointId) => {
      let currentId = startId;
      const visited = new Set();
      while (currentId && !visited.has(currentId)) {
        if (mergePointId && String(currentId) === String(mergePointId)) break;
        visited.add(currentId);
        const evt = eventById[String(currentId)];
        if (!evt || evt.eventType.toLowerCase() === 'end') break;
        branchEventIds.add(String(currentId));
        const nextEdge = edges.find(
          (ed) => String(ed.fromCadenceEventId) === String(currentId) && ed.edgeType?.toLowerCase() === 'next'
        );
        currentId = nextEdge ? nextEdge.toCadenceEventId : null;
      }
    };

    // Find all condition events and mark their branch children
    allEvents.forEach((e) => {
      if (e.eventType.toLowerCase() === 'condition') {
        // The merge point is the condition's "next" edge target
        const nextEdge = edges.find(
          (ed) => String(ed.fromCadenceEventId) === String(e.id) && ed.edgeType?.toLowerCase() === 'next'
        );
        const mergePointId = nextEdge ? String(nextEdge.toCadenceEventId) : null;
        const successEdge = edges.find(
          (ed) => String(ed.fromCadenceEventId) === String(e.id) && ed.edgeType?.toLowerCase() === 'success'
        );
        const failureEdge = edges.find(
          (ed) => String(ed.fromCadenceEventId) === String(e.id) && ed.edgeType?.toLowerCase() === 'failure'
        );
        if (successEdge) collectBranchIds(successEdge.toCadenceEventId, mergePointId);
        if (failureEdge) collectBranchIds(failureEdge.toCadenceEventId, mergePointId);
      }
    });

    // Build main event chain (exclude start, end, and branch children)
    const userEvents = allEvents
      .filter((e) => {
        const type = e.eventType.toLowerCase();
        return type !== 'start' && type !== 'end' && !branchEventIds.has(String(e.id));
      })
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
      .map((e) => {
        const step = eventToStep(e);

        // Reconstruct branches for condition steps
        if (e.eventType.toLowerCase() === 'condition') {
          const nextEdge = edges.find(
            (ed) => String(ed.fromCadenceEventId) === String(e.id) && ed.edgeType?.toLowerCase() === 'next'
          );
          const mergePointId = nextEdge ? String(nextEdge.toCadenceEventId) : null;
          const successEdge = edges.find(
            (ed) => String(ed.fromCadenceEventId) === String(e.id) && ed.edgeType?.toLowerCase() === 'success'
          );
          const failureEdge = edges.find(
            (ed) => String(ed.fromCadenceEventId) === String(e.id) && ed.edgeType?.toLowerCase() === 'failure'
          );
          step.branches = {
            true: successEdge ? followEdgeChain(successEdge.toCadenceEventId, mergePointId) : [],
            false: failureEdge ? followEdgeChain(failureEdge.toCadenceEventId, mergePointId) : [],
          };
        }

        return step;
      });
    setSteps(userEvents);

    const existingMetrics = (existingCadence.successMetrics || []).sort(
      (a, b) => (a.priority ?? 999) - (b.priority ?? 999)
    );
    if (existingMetrics.length > 0) {
      const m = existingMetrics[0];
      const metricType = normalizeKey(m.metricType || '');
      const cfg = m.metricConfig || {};
      setSuccessMetric({
        id: m.id,
        type: metricType,
        targetPercentage: m.targetPercentage ?? 0,
        isPrimary: true,
        config: {
          interactionType: cfg.interaction_type || null,
          minAmount: cfg.min_amount ? String(cfg.min_amount) : null,
          designation: cfg.designation || null,
          teamId: cfg.team_id ? String(cfg.team_id) : null,
          teamName: cfg.team_name || null,
          stageId: cfg.stage_id ? String(cfg.stage_id) : null,
          stageName: cfg.stage_name || null,
          savedSearchId: cfg.saved_search_tag_id || cfg.saved_search_id || null,
          savedSearchName: cfg.saved_search_name || null,
          savedSearchListId: cfg.saved_search_list_id ?? null,
          threshold: cfg.threshold || cfg.threshold_value || null,
        },
      });
    }

    const rules = (existingCadence.autoRemovalRules || []).map((r) => {
      const type = normalizeKey(r.removalType || '');
      return {
        id: r.id,
        type,
        config: normalizeRemovalConfig(type, r.removalConfig),
        cadenceEventId: r.cadenceEventId || null,
        terminalEvent: r.terminalEvent || null,
      };
    });
    if (rules.length > 0) setRemovalRules(rules);

    if (existingCadence.tags?.length > 0) setCampaignTags(existingCadence.tags);

    // Pre-populate linked blueprint
    if (existingCadence.sourceCadenceTemplateId) {
      const tid = String(existingCadence.sourceCadenceTemplateId);
      setCadenceTemplate(tid);
      const shell = cadenceShells.find((s) => String(s.id) === tid);
      if (shell) setTemplateSearch(shell.name);
    }

    // Restore linked proposals
    if (existingCadence.linkedProposals?.length > 0) {
      const restored = existingCadence.linkedProposals.map((lp) => {
        const pid = String(lp.proposalId);
        const match = proposals.find((p) => p.value === pid);
        return { value: pid, label: match?.label || `Proposal #${pid}` };
      });
      setLinkedProposals(restored);
    }

    // Audience state is no longer pre-populated from the existing cadence.
    // Connected audiences are shown directly from existingCadence.audiences in
    // AudienceSection. The selectedList/selectedSavedSearch/selectedContacts
    // fields now represent only net-new audiences the user is about to add.

    // Restore sender settings — prefer cadence-level fields (new), fall back to first email event
    // config for records saved before cadence-level sender config was introduced.
    const firstAutoEmail = allEvents.find((e) => e.eventType?.toLowerCase() === 'email' && e.eventConfig?.from_email);
    const fromEmail = existingCadence.senderEmail || firstAutoEmail?.eventConfig?.from_email;
    const fromName = existingCadence.senderName || firstAutoEmail?.eventConfig?.from_name;
    const replyTo = resolveReplyToEmails(existingCadence, firstAutoEmail?.eventConfig);
    if (fromName) setSenderName(fromName);
    if (fromEmail) {
      const atIdx = fromEmail.indexOf('@');
      setSenderEmailPrefix(atIdx >= 0 ? fromEmail.slice(0, atIdx) : fromEmail);
    }
    if (replyTo) setReplyToEmail(replyTo);

    if (existingCadence.singleEnrollmentOnly != null) setSingleEnrollmentOnly(existingCadence.singleEnrollmentOnly);
    if (existingCadence.enrollmentLocked != null) setEnrollmentLocked(existingCadence.enrollmentLocked);

    setPrePopulated(true);
    // Mark as "saved" so close-button doesn't falsely show the leave-confirmation modal
    if (!lastSavedAt) setLastSavedAt(new Date());

    // Load sharing settings asynchronously (fire-and-forget within the sync effect)
    if (cadenceId) {
      fetchCadenceSharingSettings(cadenceId)
        .then((sharingSettings) => {
          if (sharingSettings?.length > 0) {
            // Suppress dirty — this is background init, not a user change
            suppressNextDirtyRef.current = true;
            const userShares = sharingSettings
              .filter((s) => s.userId !== 0)
              .map((s) => ({
                shareId: s.id,
                userId: s.userId,
                permission: s.canEdit ? 'edit' : 'view',
                label: `User ${s.userId}`,
              }));
            if (userShares.length > 0) {
              setSharedUsers(userShares);
              setOriginalSharedUsers(userShares);
              // Resolve user IDs to actual names
              fetchUsersById(userShares.map((u) => u.userId))
                .then((users) => {
                  const nameMap = {};
                  (users || []).forEach((u) => {
                    nameMap[u.id] = u.name || u.email || `User ${u.id}`;
                  });
                  const resolved = userShares.map((u) => ({
                    ...u,
                    label: nameMap[u.userId] || u.label,
                  }));
                  suppressNextDirtyRef.current = true;
                  setSharedUsers(resolved);
                  setOriginalSharedUsers(resolved);
                })
                .catch((e) => console.warn('[cadences] background fetch failed:', e.message));
            }
          }
        })
        .catch((err) => {
          console.warn('[cadences] Failed to load sharing settings:', err.message);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingCadence, prePopulated, isProspectPlan]);

  // Seed senderEmailPrefix from the org's default sending address once the domain loads.
  // Only sets if empty (new cadence) — existing cadences are pre-populated above via from_email.
  useEffect(() => {
    const defaultEmail = orgDomainObj?.default_sending_email;
    if (!defaultEmail) return;
    setSenderEmailPrefix((prev) => {
      if (prev) return prev;
      const atIdx = defaultEmail.indexOf('@');
      return atIdx >= 0 ? defaultEmail.slice(0, atIdx) : prev;
    });
  }, [orgDomainObj?.default_sending_email]);

  // Enrich step-level conditions with display labels (and, for saved_search,
  // savedSearchListId) from the picker option lists once they load. The
  // backend persists only the picker value (tag_id / list_id); the label
  // — and for legacy saved_search rows the contacts list_id — comes from
  // the loaded options. This also soft-backfills savedSearchListId so the
  // next save writes it back into event_config (NEXDEV-905).
  useEffect(() => {
    const haveSavedSearches = savedSearches && savedSearches.length > 0;
    const haveContactLists = contactLists && contactLists.length > 0;
    if (!haveSavedSearches && !haveContactLists) return;

    const enrichCondition = (condition) => {
      if (!condition) return [condition, false];
      if (condition.type === 'saved_search' && haveSavedSearches && condition.savedSearch) {
        const needsName = !condition.savedSearchName;
        const needsListId = condition.savedSearchListId == null;
        if (!needsName && !needsListId) return [condition, false];
        const match = savedSearches.find((s) => String(s.value) === String(condition.savedSearch));
        if (!match) return [condition, false];
        return [
          {
            ...condition,
            savedSearchName: condition.savedSearchName || match.label,
            savedSearchListId: condition.savedSearchListId ?? match.listId ?? null,
          },
          true,
        ];
      }
      if (condition.type === 'list_membership' && haveContactLists && condition.list && !condition.listName) {
        const match = contactLists.find((l) => String(l.value) === String(condition.list));
        if (!match) return [condition, false];
        return [{ ...condition, listName: match.label }, true];
      }
      return [condition, false];
    };

    const enrichSteps = (stepList) => {
      let changed = false;
      const next = stepList.map((step) => {
        let nextStep = step;
        if (step?.type === 'condition' && step.condition) {
          const [enrichedCondition, condChanged] = enrichCondition(step.condition);
          if (condChanged) {
            nextStep = { ...step, condition: enrichedCondition };
            changed = true;
          }
        }
        // Recurse into condition branches (true/false → arrays of steps).
        if (nextStep?.branches) {
          const enrichedBranches = {};
          let branchChanged = false;
          for (const [key, branchSteps] of Object.entries(nextStep.branches)) {
            if (Array.isArray(branchSteps) && branchSteps.length > 0) {
              const [enriched, didChange] = enrichSteps(branchSteps);
              enrichedBranches[key] = enriched;
              if (didChange) branchChanged = true;
            } else {
              enrichedBranches[key] = branchSteps;
            }
          }
          if (branchChanged) {
            nextStep = { ...nextStep, branches: enrichedBranches };
            changed = true;
          }
        }
        return nextStep;
      });
      return [changed ? next : stepList, changed];
    };

    suppressNextDirtyRef.current = true;
    setSteps((prev) => {
      const [next, changed] = enrichSteps(prev);
      if (!changed) suppressNextDirtyRef.current = false; // nothing changed, no re-render coming
      return changed ? next : prev;
    });
    // existingCadence is a dep because the cadence load may complete after
    // the picker option lists; without it, this effect never re-fires for
    // the freshly populated steps and saved-search names stay blank.
  }, [savedSearches, contactLists, existingCadence]);

  // Re-resolve linked proposal labels once the real name is known. The initial
  // hydration above only matches against the already-loaded `proposals`
  // browse list (capped/sorted), so a linked proposal outside that window
  // never resolved past "Proposal #<id>" — this also fetches it by id
  // directly (NEXDEV-2120).
  const resolvedProposalLabels = useProposalLabels(
    linkedProposals.map((lp) => lp.value),
    proposals
  );
  useEffect(() => {
    setLinkedProposals((prev) => {
      const next = prev.map((lp) =>
        resolvedProposalLabels[lp.value] && resolvedProposalLabels[lp.value] !== lp.label
          ? { ...lp, label: resolvedProposalLabels[lp.value] }
          : lp
      );
      const changed = next.some((lp, i) => lp !== prev[i]);
      if (changed) suppressNextDirtyRef.current = true;
      return changed ? next : prev;
    });
  }, [resolvedProposalLabels]);

  // Fetch contact count when a list is selected (uses search API like the list page)
  useEffect(() => {
    if (!selectedList?.value) return;
    Api.CONTACTS.SEARCH.post({
      params: { offset: 0, limit: 0 },
      data: JSON.stringify({ must: [{ 'list_memberships.list_id': { match: String(selectedList.value) } }] }),
    })
      .then((resp) => {
        const total = resp?.total ?? 0;
        suppressNextDirtyRef.current = true;
        setSelectedList((prev) => (prev?.value === selectedList.value ? { ...prev, count: total } : prev));
      })
      .catch((e) => console.warn('[cadences] background fetch failed:', e.message));
  }, [selectedList?.value]);

  // Re-resolve blueprint name when cadenceShells finishes loading (race condition fix)
  useEffect(() => {
    if (existingCadence?.sourceCadenceTemplateId && cadenceShells?.length > 0) {
      const shell = cadenceShells.find((s) => String(s.id) === String(existingCadence.sourceCadenceTemplateId));
      if (shell) {
        suppressNextDirtyRef.current = true;
        setCadenceTemplate(String(shell.id));
        setTemplateSearch(shell.name);
      }
    }
  }, [cadenceShells?.length, existingCadence?.sourceCadenceTemplateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-load steps from a blueprint when navigated with shellId
  useEffect(() => {
    if (shellId && !cadenceId && !prePopulated) {
      setCadenceTemplate(String(shellId));
      loadStepsFromShell(shellId);
      const shell = cadenceShells.find((s) => String(s.id) === String(shellId));
      if (shell) {
        setTemplateSearch(shell.name);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shellId, cadenceId, cadenceShells.length]);

  // Enable dirty tracking after initial cadence data has loaded
  useEffect(() => {
    if (!prePopulated) return;
    const t = setTimeout(() => {
      dirtyEnabledRef.current = true;
    }, 50);
    return () => clearTimeout(t);
  }, [prePopulated]);

  // Mark dirty when any user-editable field changes after initial load
  useEffect(() => {
    if (!dirtyEnabledRef.current) return;
    const curr = {
      steps,
      cadenceName,
      successMetric,
      additionalMetrics,
      selectedList,
      selectedSavedSearch,
      selectedContacts,
      singleEnrollmentOnly,
      enrollmentLocked,
      senderEmailPrefix,
      senderName,
      replyToEmail,
      sharedUsers,
      linkedProposals,
      cadenceTemplate,
      campaignTags,
      removalRules,
    };
    const prev = prevDirtyValsRef.current;
    if (prev) {
      const changed = Object.entries(curr)
        .filter(([k, v]) => v !== prev[k])
        .map(([k]) => k);
      // eslint-disable-next-line no-console
      console.log('[cadences] dirty field(s):', changed.join(', '), { suppress: suppressNextDirtyRef.current });
    }
    prevDirtyValsRef.current = curr;
    if (suppressNextDirtyRef.current) {
      suppressNextDirtyRef.current = false;
      return;
    }
    setIsDirty(true);
  }, [
    steps,
    cadenceName,
    successMetric,
    additionalMetrics,
    selectedList,
    selectedSavedSearch,
    selectedContacts,
    singleEnrollmentOnly,
    enrollmentLocked,
    senderEmailPrefix,
    senderName,
    replyToEmail,
    sharedUsers,
    linkedProposals,
    cadenceTemplate,
    campaignTags,
    removalRules,
  ]);

  // Warn before browser close/refresh/external navigate when there are unsaved changes
  useEffect(() => {
    if (!isDirty || !effectiveCadenceId) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, effectiveCadenceId]);

  // Intercept browser back button (popstate) when there are unsaved changes.
  // Strategy: push a duplicate history entry once when dirty so the first back-press
  // stays on the same URL. The popstate handler re-pushes the guard and shows the
  // modal instead of letting the SPA route away.
  const guardPushedRef = useRef(false);
  useEffect(() => {
    if (!isDirty || !effectiveCadenceId) return;
    if (!guardPushedRef.current) {
      history.pushState(null, '', window.location.href);
      guardPushedRef.current = true;
    }
    const handlePopState = () => {
      history.pushState(null, '', window.location.href);
      setShowLeaveConfirm(true);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty, effectiveCadenceId]);

  // Step 2: drawer state
  const [selectedStepIndex, setSelectedStepIndex] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // Get the selected step - either from main flow or from a branch array
  const selectedStep =
    selectedBranch !== null
      ? (steps[selectedBranch.stepIndex]?.branches?.[selectedBranch.branchKey]?.[selectedBranch.branchStepIndex] ??
        null)
      : selectedStepIndex !== null
        ? steps[selectedStepIndex]
        : null;

  const navigateAway = () => {
    if (isBlueprint) {
      if (effectiveCadenceId) {
        EverTrue.Navigator(`/cadences-v2/template/${effectiveCadenceId}`, true);
      } else {
        EverTrue.Navigator('/cadences-v2?tab=shells', true);
      }
    } else {
      EverTrue.Navigator('/cadences-v2', true);
    }
  };

  /** Close button handler — confirm if there are unsaved changes. */
  const handleClose = () => {
    if (isSaving || namePromptOpen || showPublishConfirm) return;
    // Existing cadence: use reactive dirty flag
    if (effectiveCadenceId && isDirty) {
      setShowLeaveConfirm(true);
      return;
    }
    // New unsaved cadence: any content typed before first save
    const hasUnsavedChanges =
      !lastSavedAt && !effectiveCadenceId && (cadenceName !== defaultName || steps.length > 0 || !!successMetric?.type);
    if (hasUnsavedChanges) {
      setShowLeaveConfirm(true);
    } else {
      navigateAway();
    }
  };

  const handleNextStep = () => {
    if (currentStep === 1 && !cadenceName.trim()) {
      EverTrue.Alert.error(`Please enter a ${typeLabel.toLowerCase()} name`);
      return;
    }
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      setSelectedStepIndex(null);
      setSelectedBranch(null);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setSelectedStepIndex(null);
      setSelectedBranch(null);
    }
  };

  /**
   * Map a UI condition object to the backend event_config for a CONDITION event.
   *
   * Only writes keys the BE evaluator reads. Display labels (portfolio_team_name,
   * saved_search_name, list_name, saved_search_id) stay in FE state and are
   * looked up from pickers on render. Email/interaction/gift conditions scope
   * by cadence_event_id (set automatically by the BE), not a `days` window.
   */
  const buildConditionConfig = (step) => {
    const cond = step.condition;
    if (!cond?.type) {
      const condType = step.conditionType || 'always_true';
      return { condition_type: condType };
    }

    switch (cond.type) {
      case 'email_opened':
      case 'email_replied':
      case 'email_clicked':
        return { condition_type: cond.type };
      case 'interaction_created':
        return {
          condition_type: 'interaction_created',
          interaction_type: cond.interactionType || null,
        };
      case 'gift_given':
        return {
          condition_type: 'gift_given',
          min_amount: cond.minAmount ? parseFloat(cond.minAmount) : null,
          designation: cond.designation || null,
        };
      case 'portfolio_stage_reached':
        return {
          condition_type: 'portfolio_stage_reached',
          portfolio_team_id: cond.portfolioTeamId || null,
          portfolio_stage: cond.portfolioStage || null,
        };
      case 'saved_search': {
        const out = {
          condition_type: 'saved_search',
          saved_search_tag_id: cond.savedSearch ? parseInt(cond.savedSearch, 10) : null,
        };
        if (cond.savedSearchListId != null) out.saved_search_list_id = Number(cond.savedSearchListId);
        return out;
      }
      case 'list_membership':
        return { condition_type: 'list_membership', list_id: cond.list || null };
      default:
        return { condition_type: 'always_true' };
    }
  };

  /** Build the events array from the UI steps. */
  const buildEventsInput = () =>
    steps.map((step, i) => {
      const isTask = step.type === 'task';
      // Send EMAIL with isAutomated=false for manual tasks — backend converts to TASK internally.
      // Avoids sending TASK enum which may not be in the gateway schema yet.
      const eventType = isTask ? 'EMAIL' : (step.type || 'EMAIL').toUpperCase();
      const base = {
        sequenceOrder: i + 1,
        eventType,
        isActive: true,
        eventConfig: null,
      };
      if (eventType === 'EMAIL') {
        base.isAutomated = !isTask;
        if (isTask) {
          const taskActionType = step.taskType === 'email' ? 'send_email' : step.taskType || 'other';
          base.eventConfig = {
            title: step.name || 'Manual task',
            action_type: taskActionType,
            name: step.name || null,
            description: step.description || null,
            ...(step.skipBusinessDayCheck ? { skip_business_day_check: true } : {}),
          };
        } else {
          const userEmail = SessionStore.getUser()?.email || '';
          const fromEmail = senderEmailPrefix ? `${senderEmailPrefix}@${orgDomain}` : userEmail;
          const body = step.emailBody || '';
          base.eventConfig = {
            name: step.name || null,
            description: step.description || null,
            from_email: fromEmail,
            from_name: senderName || null,
            reply_to_emails: parseReplyToEmails(replyToEmail),
            subject: step.subjectOverride || '',
            html_body: body,
            text_body: new DOMParser().parseFromString(body, 'text/html').body.textContent || '',
            email_template_id: step.template?.id ? String(step.template.id) : null,
            template_name: step.template?.name || null,
            signature_id: step.signature?.id || null,
            signature_name: step.signature?.signature_name || null,
            attachments: step.attachments || [],
            ...(step.skipBusinessDayCheck ? { skip_business_day_check: true } : {}),
          };
        }
      }
      if (eventType === 'WAIT') {
        if (step.waitType === 'until_date') {
          base.eventConfig = {
            name: step.name || null,
            description: step.description || null,
            wait_type: 'until_date',
            duration_minutes: 0,
            duration_hours: 0,
            duration_days: 0,
            wait_until: step.waitUntil || null,
          };
        } else {
          const unit = step.waitUnit || 'days';
          const duration = step.waitDuration || 1;
          base.eventConfig = {
            name: step.name || null,
            description: step.description || null,
            wait_type: 'duration',
            duration_minutes: unit === 'minutes' ? duration : 0,
            duration_hours: unit === 'hours' ? duration : 0,
            duration_days: unit === 'days' ? duration : unit === 'weeks' ? duration * 7 : 0,
          };
        }
      }
      if (eventType === 'CONDITION') {
        base.eventConfig = {
          ...buildConditionConfig(step),
          name: step.name || null,
          description: step.description || null,
        };
      }
      return base;
    });

  /** Build an event input object for a single branch sub-step. */
  const buildBranchStepInput = (step, seqOrder) => {
    const isTask = step.type === 'task';
    const eventType = isTask ? 'EMAIL' : (step.type || 'EMAIL').toUpperCase();
    const base = {
      sequenceOrder: seqOrder,
      eventType,
      isActive: true,
      eventConfig: null,
    };
    if (eventType === 'EMAIL') {
      base.isAutomated = !isTask;
      if (isTask) {
        const taskActionType = step.taskType === 'email' ? 'send_email' : step.taskType || 'other';
        base.eventConfig = {
          title: step.name || 'Manual task',
          action_type: taskActionType,
          name: step.name || null,
          description: step.description || null,
          ...(step.skipBusinessDayCheck ? { skip_business_day_check: true } : {}),
        };
      } else {
        const userEmail = SessionStore.getUser()?.email || '';
        const fromEmail = senderEmailPrefix ? `${senderEmailPrefix}@${orgDomain}` : userEmail;
        const body = step.emailBody || '';
        base.eventConfig = {
          name: step.name || null,
          description: step.description || null,
          from_email: fromEmail,
          from_name: senderName || null,
          reply_to_emails: parseReplyToEmails(replyToEmail),
          subject: step.subjectOverride || '',
          html_body: body,
          text_body: new DOMParser().parseFromString(body, 'text/html').body.textContent || '',
          email_template_id: step.template?.id ? String(step.template.id) : null,
          template_name: step.template?.name || null,
          signature_id: step.signature?.id || null,
          signature_name: step.signature?.signature_name || null,
          attachments: step.attachments || [],
          ...(step.skipBusinessDayCheck ? { skip_business_day_check: true } : {}),
        };
      }
    }
    if (eventType === 'WAIT') {
      if (step.waitType === 'until_date') {
        base.eventConfig = {
          name: step.name || null,
          description: step.description || null,
          wait_type: 'until_date',
          duration_minutes: 0,
          duration_hours: 0,
          duration_days: 0,
          wait_until: step.waitUntil || null,
        };
      } else {
        const unit = step.waitUnit || 'days';
        const duration = step.waitDuration || 1;
        base.eventConfig = {
          name: step.name || null,
          description: step.description || null,
          wait_type: 'duration',
          duration_minutes: unit === 'minutes' ? duration : 0,
          duration_hours: unit === 'hours' ? duration : 0,
          duration_days: unit === 'days' ? duration : unit === 'weeks' ? duration * 7 : 0,
        };
      }
    }
    return base;
  };

  /**
   * Build a branch map from the current steps.
   * Returns an array of { stepIndex, trueBranch, falseBranch } for condition steps with branches.
   */
  const buildBranchMap = () => {
    const branches = [];
    // Branch events must have sequenceOrder values that don't collide with
    // the main flow (1..steps.length). Start numbering after the main flow
    // and increment across all branches so every event has a unique sequence.
    let nextSeq = steps.length + 1;
    steps.forEach((step, i) => {
      if (step.type !== 'condition' || !step.branches) return;
      const trueBranch = (step.branches.true || []).map((bs) => {
        const evt = buildBranchStepInput(bs, nextSeq);
        nextSeq++;
        return evt;
      });
      const falseBranch = (step.branches.false || []).map((bs) => {
        const evt = buildBranchStepInput(bs, nextSeq);
        nextSeq++;
        return evt;
      });
      if (trueBranch.length > 0 || falseBranch.length > 0) {
        branches.push({ stepIndex: i, trueBranch, falseBranch });
      }
    });
    return branches;
  };

  /** Save changes to an existing cadence atomically via updateCadenceWithConfig. */
  const handleSaveExisting = async (saveName, status, { skipGuard = false } = {}) => {
    if (!skipGuard) {
      if (savingRef.current) return;
      savingRef.current = true;
      setIsSaving(true);
    }
    try {
      const cadenceInput = {
        name: saveName,
        tags: campaignTags,
        sourceCadenceTemplateId: cadenceTemplate ? parseInt(cadenceTemplate, 10) : null,
        singleEnrollmentOnly,
        enrollmentLocked,
        senderEmail: senderEmailPrefix ? `${senderEmailPrefix}@${orgDomain}` : undefined,
        senderName: senderName || undefined,
        replyToEmails: parseReplyToEmails(replyToEmail),
      };
      // Only set status for explicit draft transitions — activation is handled by activateCadence
      if (status != null && status !== 'active') {
        cadenceInput.status = status;
      }

      const result = await cadenceGqlCall(UPDATE_CADENCE_WITH_CONFIG_MUTATION, {
        cadenceId: String(effectiveCadenceId),
        version: getVersion(),
        input: {
          cadence: cadenceInput,
          events: buildEventsWithRefIds(),
          branches: buildBranchesForConfig(),
          successMetrics: buildSuccessMetricsInput(),
          autoRemovalRules: buildAutoRemovalRulesInput(),
          sharing: buildSharingInput(),
          proposalIds: linkedProposals.map((p) => parseInt(p.value, 10)).filter((n) => !isNaN(n)),
          audiences: buildAudiencesInput(),
        },
      });

      const saveResult = result?.updateCadenceWithConfig;
      if (!saveResult?.success) {
        throw new Error(saveResult?.message || 'Failed to update cadence');
      }
      if (saveResult.version) trackVersion(saveResult.version);

      // Activate before enrollment so Temporal workflows see "active" status.
      if (status === 'active') {
        const activateResult = await activateCadence(effectiveCadenceId, getVersion());
        if (!activateResult?.success) {
          throw new Error(activateResult?.message || 'Failed to activate cadence');
        }
      }

      // Enroll any pending audience selections. Disconnects are handled immediately via
      // deleteCadenceAudience; this only fires when the user has added new recipients.
      if (status === 'active' || hasPendingAudience()) {
        await enrollRecipients(effectiveCadenceId);
        setSelectedList(null);
        setSelectedSavedSearch(null);
        setSelectedContacts([]);
      }

      setLastSavedAt(new Date());
      setIsDirty(false);
      if (status === 'active') {
        EverTrue.Alert.success(`${typeLabel} published successfully!`);
        navigateAway();
      } else {
        EverTrue.Alert.success(`Saved as "${saveName}"`);
      }
    } catch (err) {
      if (err.code === 'ATTACHMENT_TOO_LARGE') {
        EverTrue.Alert.error(
          existingCadence?.status === 'paused'
            ? 'Activation Failed: One or more automated email steps include an attachment that is over the 7MB limit. This cadence cannot be re-activated.'
            : 'Publishing Failed: One or more automated email steps include an attachment that is over the 7MB limit. Please remove / reduce the size of these attachments in order to publish.'
        );
      } else {
        EverTrue.Alert.error(`Failed to save: ${formatSaveError(err)}`);
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  /**
   * Turn a backend error (with optional machine-readable code) into a
   * user-friendly message. Prefer err.code over message-string matching —
   * codes are stable; messages change with refactors.
   */
  const formatSaveError = (err) => {
    if (!err) return 'An unexpected error occurred.';
    const code = err.code;
    const message = typeof err === 'string' ? err : err.message || '';

    // Code-first: stable, set by the backend ErrorCodeExtension
    if (code === 'MISSING_EMAIL_CONTENT')
      return 'One or more automated email steps have no content. Add a template or compose subject and body inline before activating.';
    if (code === 'INVALID_STATUS_TRANSITION')
      return 'This cadence cannot be activated in its current state. Refresh and try again.';
    if (code === 'VERSION_CONFLICT')
      return 'Someone else updated this cadence at the same time. Refresh and reapply your changes.';
    if (code === 'DUPLICATE_REFERENCE') return 'A cadence with this ID already exists. Use a unique reference ID.';
    if (code === 'PERMISSION_DENIED') return 'You do not have permission to perform this action.';
    // VALIDATION_ERROR covers the typed-config rejections from the backend's
    // metric_config / removal_config schemas. UI gating in handlePublish
    // catches the common cases; this is the safety net for anything that
    // slips past, with type-specific copy where the BE message hints at it.
    if (code === 'VALIDATION_ERROR') {
      if (message.includes("'saved_search'"))
        return 'A saved-search metric or removal rule is missing a saved search. Pick one before publishing.';
      if (message.includes("'portfolio_stage_reached'"))
        return 'A portfolio-stage metric or removal rule is missing a team or stage. Fill both before publishing.';
      if (message.includes("'cadence_qualification'"))
        return 'A cadence-qualification removal rule is missing the target cadence.';
      if (message.includes("'list_membership'")) return 'A list-membership removal or condition is missing a list.';
      if (message.includes("'gifts_amount'")) return 'A gifts-amount metric is missing a target threshold.';
      return `A configuration value is invalid: ${message}`;
    }

    // Fallback: message-string matching for errors without a specific code
    if (!message) return 'An unexpected error occurred.';
    if (message.includes('html_body') || message.includes('text_body'))
      return 'One or more email steps are missing a body. Go back and add content to each email step.';
    if (message.includes('subject'))
      return 'One or more email steps are missing a subject line. Go back and add a subject to each email step.';
    if (message.includes('from_email'))
      return 'One or more email steps are missing a sender address. Check your email configuration.';
    if (message.includes('validation error') || message.includes('EmailEventConfig'))
      return 'One or more email steps have incomplete configuration. Go back and fill in the subject and body for each email.';
    return message;
  };

  /** After creating a cadence, enroll recipients based on the current selection method. */
  const enrollRecipients = async (cadenceId) => {
    const method = selectionMethod?.value;
    try {
      if (isProspectPlan && selectedContacts.length > 0) {
        const enrollResult = await enrollMember(cadenceId, String(selectedContacts[0].id));
        if (enrollResult?.success) {
          EverTrue.Alert.success('Contact enrolled');
        } else {
          EverTrue.Alert.error(enrollResult?.message || 'Failed to enroll contact');
        }
      } else if ((method === 'contacts' || isProspectPlan) && selectedContacts.length > 0) {
        const contactIds = selectedContacts.map((c) => c.id);
        await saveIndividualCadenceAudiences(cadenceId, contactIds);
        const result = await bulkEnrollMembers(cadenceId, contactIds);
        if (result?.success) {
          const count = result.job?.totalItems || contactIds.length;
          EverTrue.Alert.info(`Enrolling ${count} contact(s) in the background`);
        } else {
          EverTrue.Alert.warn(result?.message || 'Some contacts may not have been enrolled');
        }
      } else if (method === 'list' && selectedList) {
        const result = await createCadenceListAudience(cadenceId, selectedList.value);
        if (result?.success) {
          EverTrue.Alert.info(`Importing contacts from list "${selectedList.label}"`);
        } else {
          EverTrue.Alert.warn(result?.message || 'Failed to attach list audience');
        }
      } else if (method === 'search' && selectedSavedSearch) {
        const result = await createCadenceAudience(cadenceId, selectedSavedSearch.value, selectedSavedSearch.listId);
        if (result?.success) {
          EverTrue.Alert.info(`Importing contacts from saved search "${selectedSavedSearch.label}"`);
        } else {
          EverTrue.Alert.warn(result?.message || 'Failed to attach saved search audience');
        }
      }
    } catch (err) {
      EverTrue.Alert.error(`Enrollment failed: ${err.message}`);
    }
  };

  /** Build backend metric_config from the new metric state shape.
   *
   * Only writes keys the BE evaluator reads — display labels stay in FE state.
   */
  const buildMetricConfig = (m) => {
    const cfg = m.config || {};
    const metricConfig = {};
    if (m.type === 'interaction_created' && cfg.interactionType) metricConfig.interaction_type = cfg.interactionType;
    if (m.type === 'gift_given') {
      if (cfg.minAmount) metricConfig.min_amount = Number(String(cfg.minAmount).replace(/,/g, ''));
      if (cfg.designation) metricConfig.designation = cfg.designation;
    }
    if (m.type === 'portfolio_stage_reached') {
      if (cfg.teamId) metricConfig.team_id = cfg.teamId;
      if (cfg.stageId) metricConfig.stage_id = cfg.stageId;
      if (cfg.stageName) metricConfig.stage_name = cfg.stageName;
    }
    if (m.type === 'saved_search') {
      if (cfg.savedSearchId) metricConfig.saved_search_tag_id = cfg.savedSearchId;
      if (cfg.savedSearchName) metricConfig.saved_search_name = cfg.savedSearchName;
      if (cfg.savedSearchListId != null) metricConfig.saved_search_list_id = Number(cfg.savedSearchListId);
    }
    if (m.type === 'gifts_amount' || m.type === 'gifts_count' || m.type === 'meetings_count') {
      if (cfg.threshold) metricConfig.threshold = Number(cfg.threshold);
    }
    return metricConfig;
  };

  /**
   * Returns a stable referenceId for a step — used by both buildEventsWithRefIds and
   * buildBranchesForConfig so condition events and their branch conditionReferenceId always agree.
   * Prefers the backend-assigned referenceId (UUID), falls back to the step's local id.
   */
  const getStepRefId = (step, index) => {
    if (step?.referenceId) return String(step.referenceId);
    if (step?.type === 'condition') return String(step.id) || `condition-${index}`;
    return null;
  };

  /** Build events array with referenceIds for createCadenceWithConfig / updateCadenceWithConfig. */
  const buildEventsWithRefIds = () =>
    buildEventsInput().map((evt, i) => {
      const ref = getStepRefId(steps[i], i);
      return ref ? { ...evt, referenceId: ref } : evt;
    });

  /** Build branches array in CadenceConfigBranchInput format from current steps. */
  const buildBranchesForConfig = () => {
    const result = [];
    for (const { stepIndex, trueBranch, falseBranch } of buildBranchMap()) {
      const conditionReferenceId = getStepRefId(steps[stepIndex], stepIndex) || `condition-${stepIndex}`;
      if (trueBranch.length > 0) result.push({ conditionReferenceId, edgeType: 'SUCCESS', events: trueBranch });
      if (falseBranch.length > 0) result.push({ conditionReferenceId, edgeType: 'FAILURE', events: falseBranch });
    }
    return result;
  };

  /** Build successMetrics array for createCadenceWithConfig. */
  const buildSuccessMetricsInput = () => {
    const allMetrics = successMetric?.type
      ? [successMetric, ...additionalMetrics.filter((m) => m?.type)]
      : additionalMetrics.filter((m) => m?.type);
    return allMetrics.map((m, mi) => {
      const metricConfig = buildMetricConfig(m);
      return {
        metricType: m.type,
        targetPercentage: m.targetPercentage ?? null,
        metricConfig: Object.keys(metricConfig).length > 0 ? metricConfig : null,
        priority: mi,
      };
    });
  };

  /** Build autoRemovalRules array for createCadenceWithConfig. */
  const buildAutoRemovalRulesInput = () =>
    removalRules
      .filter((r) => r.type)
      .map((r, i) => {
        const removalConfig = buildRemovalConfig(r);
        return {
          removalType: r.type,
          removalConfig: Object.keys(removalConfig).length > 0 ? removalConfig : null,
          priority: i,
        };
      });

  /** Build sharing array for createCadenceWithConfig. */
  const buildSharingInput = () => {
    const shares = [];
    for (const share of sharedUsers) {
      const canEdit = share.permission === 'edit';
      shares.push({
        userId: Number(share.userId),
        canView: true,
        canEdit,
        shareLevel: canEdit ? 'editable' : 'viewable',
      });
    }
    return shares;
  };

  // Audiences are now managed via dedicated disconnect/enroll mutations (deleteCadenceAudience,
  // createCadenceListAudience, etc.) rather than through the config mutation. Omit the audiences
  // field from updateCadenceWithConfig to avoid accidentally clearing connected audiences.
  const buildAudiencesInput = () => undefined;

  const hasPendingAudience = () => {
    const method = selectionMethod?.value;
    return (
      selectedContacts.length > 0 ||
      (method === 'search' && !!selectedSavedSearch?.value) ||
      (method === 'list' && !!selectedList?.value)
    );
  };

  const handlePublish = async (nameOverride) => {
    if (isSaving || savingRef.current) return;
    savingRef.current = true; // Set immediately to prevent double-clicks
    setIsSaving(true);
    const saveName = nameOverride || cadenceName;
    if (steps.length === 0) {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error(`Please add at least one step to your ${typeLabel.toLowerCase()}`);
      return;
    }
    if (!successMetric?.type) {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error('At least one success metric is required before publishing.');
      return;
    }
    if (successMetric.targetPercentage === 0) {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error('Success metric target percentage must be at least 1%. Please update it before publishing.');
      return;
    }
    // Check top-level AND branch email steps for missing subject/body
    const allEmailSteps = collectEmailSteps(steps);
    const incompleteEmailSteps = getIncompleteEmailSteps(steps, { requireAutomationEnabled: false });
    if (incompleteEmailSteps.length > 0) {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error(
        'All automated email steps must have a subject line and body content before publishing. Check condition branches too.'
      );
      return;
    }
    const overlongTaskSteps = getOverlongTaskSteps(steps);
    if (overlongTaskSteps.length > 0) {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error(
        `Manual task step name must be ${TASK_STEP_NAME_MAX_LENGTH} characters or fewer and description must be ${TASK_STEP_DESCRIPTION_MAX_LENGTH} characters or fewer.`
      );
      return;
    }
    const configIssues = findIncompleteConfigs({ successMetric, additionalMetrics, removalRules, steps });
    if (configIssues.length > 0) {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error(`Cannot publish — ${configIssues.join('; ')}.`);
      return;
    }
    if (allEmailSteps.length > 0 && domainStatus === 'not_configured') {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error(
        'Email sending requires a verified domain. Ask your organization Owner to configure a sending domain in Organization Settings.'
      );
      return;
    }
    if (allEmailSteps.length > 0 && domainStatus === 'pending') {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error(
        "Your organization's sending domain is not yet verified. DNS verification can take up to 48 hours. Try again once verification is complete."
      );
      return;
    }
    if (senderEmailPrefix.includes('@')) {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error('Sender email must not include "@" or a domain — enter only the part before the @.');
      return;
    }
    if (effectiveCadenceId && existingCadence) {
      return handleSaveExisting(saveName, 'active', { skipGuard: true });
    }
    try {
      const result = await cadenceGqlCall(CREATE_CADENCE_WITH_CONFIG_MUTATION, {
        input: {
          cadence: {
            name: saveName,
            cadenceType,
            status: 'active',
            tags: campaignTags,
            sourceCadenceTemplateId: cadenceTemplate ? parseInt(cadenceTemplate, 10) : undefined,
            singleEnrollmentOnly,
            enrollmentLocked,
            senderEmail: senderEmailPrefix ? `${senderEmailPrefix}@${orgDomain}` : undefined,
            senderName: senderName || undefined,
            replyToEmails: parseReplyToEmails(replyToEmail),
          },
          events: buildEventsWithRefIds(),
          branches: buildBranchesForConfig(),
          successMetrics: buildSuccessMetricsInput(),
          autoRemovalRules: buildAutoRemovalRulesInput(),
          sharing: buildSharingInput(),
          proposalIds: linkedProposals.map((p) => parseInt(p.value, 10)).filter((n) => !isNaN(n)),
        },
      });
      const createResult = result?.createCadenceWithConfig;
      if (createResult?.success) {
        const newId = createResult.cadence?.id;
        if (newId) {
          setSavedCadenceId(newId);
          await enrollRecipients(newId);
        }
        EverTrue.Alert.success(`${typeLabel} published successfully!`);
        EverTrue.Navigator('/cadences-v2', true);
      } else {
        EverTrue.Alert.error(formatSaveError(createResult?.message) || `Failed to publish ${typeLabel.toLowerCase()}`);
      }
    } catch (err) {
      if (err.code === 'ATTACHMENT_TOO_LARGE') {
        EverTrue.Alert.error(
          'Publishing Failed: One or more automated email steps include an attachment that is over the 7MB limit. Please remove / reduce the size of these attachments in order to publish.'
        );
      } else {
        EverTrue.Alert.error(`Failed to publish: ${formatSaveError(err)}`);
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleSaveDraft = async (nameOverride) => {
    if (isSaving || savingRef.current) return;
    savingRef.current = true; // Set immediately to prevent double-clicks
    setIsSaving(true);
    const saveName = nameOverride || cadenceName;
    if (!saveName.trim()) {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error(`Please enter a ${typeLabel.toLowerCase()} name`);
      return;
    }
    // Validate automated email steps have subject/body before save
    const incompleteEmails = getIncompleteEmailSteps(steps);
    if (incompleteEmails.length > 0) {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error('All automated email steps must have a subject line and body content before saving.');
      return;
    }
    const overlongTaskSteps = getOverlongTaskSteps(steps);
    if (overlongTaskSteps.length > 0) {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error(
        `Manual task step name must be ${TASK_STEP_NAME_MAX_LENGTH} characters or fewer and description must be ${TASK_STEP_DESCRIPTION_MAX_LENGTH} characters or fewer.`
      );
      return;
    }
    if (senderEmailPrefix.includes('@')) {
      savingRef.current = false;
      setIsSaving(false);
      EverTrue.Alert.error('Sender email must not include "@" or a domain — enter only the part before the @.');
      return;
    }
    if (effectiveCadenceId && existingCadence) {
      savingRef.current = false;
      setIsSaving(false);
      return handleSaveExisting(saveName, isBlueprint ? null : 'draft');
    }
    try {
      const result = await cadenceGqlCall(CREATE_CADENCE_WITH_CONFIG_MUTATION, {
        input: {
          cadence: {
            name: saveName,
            cadenceType,
            status: 'draft',
            tags: campaignTags,
            isTemplate: isNewBlueprint || undefined,
            sourceCadenceTemplateId: cadenceTemplate ? parseInt(cadenceTemplate, 10) : undefined,
            singleEnrollmentOnly,
            enrollmentLocked,
            senderEmail: senderEmailPrefix ? `${senderEmailPrefix}@${orgDomain}` : undefined,
            senderName: senderName || undefined,
            replyToEmails: parseReplyToEmails(replyToEmail),
          },
          events: steps.length > 0 ? buildEventsWithRefIds() : [],
          branches: buildBranchesForConfig(),
          successMetrics: buildSuccessMetricsInput(),
          autoRemovalRules: buildAutoRemovalRulesInput(),
          sharing: buildSharingInput(),
          proposalIds: linkedProposals.map((p) => parseInt(p.value, 10)).filter((n) => !isNaN(n)),
        },
      });
      const createResult = result?.createCadenceWithConfig;
      if (createResult?.success) {
        const newId = createResult.cadence?.id;
        if (newId) {
          setSavedCadenceId(newId);
          if (!isNewBlueprint) {
            await enrollRecipients(newId);
            setSelectedList(null);
            setSelectedSavedSearch(null);
            setSelectedContacts([]);
          }
        }
        setLastSavedAt(new Date());
        setIsDirty(false);
        EverTrue.Alert.success(`Saved as "${saveName}"`);
        // Update URL so subsequent saves update instead of creating duplicates (stays in creation flow)
        EverTrue.Navigator(`/cadences-v2/${newId}/edit`, { trigger: true, replace: true });
      } else {
        EverTrue.Alert.error(
          formatSaveError(createResult?.message) || `Failed to save ${typeLabel.toLowerCase()} draft`
        );
      }
    } catch (err) {
      EverTrue.Alert.error(`Failed to save: ${formatSaveError(err)}`);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  /**
   * Save settings for a live (active/paused) cadence — updates metadata, metrics, rules,
   * sharing, and proposals atomically. Does NOT modify the event flow (events: omitted → skip).
   */
  const handleSaveSettings = async (nameOverride) => {
    if (isSaving || savingRef.current || !effectiveCadenceId || !existingCadence) return;
    if (senderEmailPrefix.includes('@')) {
      EverTrue.Alert.error('Sender email must not include "@" or a domain — enter only the part before the @.');
      return;
    }
    savingRef.current = true;
    setIsSaving(true);
    const saveName = nameOverride || cadenceName;
    try {
      const result = await cadenceGqlCall(UPDATE_CADENCE_WITH_CONFIG_MUTATION, {
        cadenceId: String(effectiveCadenceId),
        version: getVersion(),
        input: {
          cadence: {
            name: saveName,
            tags: campaignTags,
            sourceCadenceTemplateId: cadenceTemplate ? parseInt(cadenceTemplate, 10) : null,
            singleEnrollmentOnly,
            enrollmentLocked,
            senderEmail: senderEmailPrefix ? `${senderEmailPrefix}@${orgDomain}` : undefined,
            senderName: senderName || undefined,
            replyToEmails: parseReplyToEmails(replyToEmail),
          },
          // events/branches omitted → backend leaves the event flow untouched
          successMetrics: buildSuccessMetricsInput(),
          autoRemovalRules: buildAutoRemovalRulesInput(),
          sharing: buildSharingInput(),
          proposalIds: linkedProposals.map((p) => parseInt(p.value, 10)).filter((n) => !isNaN(n)),
          audiences: buildAudiencesInput(),
        },
      });

      const saveResult = result?.updateCadenceWithConfig;
      if (!saveResult?.success) {
        throw new Error(saveResult?.message || 'Failed to update cadence');
      }
      if (saveResult.version) trackVersion(saveResult.version);

      // Enroll any pending audience selections. Disconnects are handled immediately via
      // deleteCadenceAudience; this only fires when the user has added new recipients.
      if (hasPendingAudience()) {
        await enrollRecipients(effectiveCadenceId);
        setSelectedList(null);
        setSelectedSavedSearch(null);
        setSelectedContacts([]);
      }

      // Save step content edits (name, description, template) for each changed step.
      // Only sends event_config — structural fields (event_type, sequence_order) are omitted
      // so the backend's active-cadence guard doesn't reject the request.
      {
        const existingEventMap = {};
        (existingCadence.events || []).forEach((e) => {
          existingEventMap[String(e.id)] = e;
        });
        const flattenSteps = (stepList) => {
          const out = [];
          (stepList || []).forEach((s) => {
            out.push(s);
            if (s.branches) {
              out.push(...flattenSteps(s.branches.true));
              out.push(...flattenSteps(s.branches.false));
            }
          });
          return out;
        };
        const allCurrentSteps = flattenSteps(steps);
        for (const step of allCurrentSteps) {
          if (!step.id) continue;
          const existing = existingEventMap[String(step.id)];
          if (!existing) continue;
          const existingName = existing.eventConfig?.name || '';
          const existingDesc = existing.eventConfig?.description || '';
          const existingTemplateId = existing.eventConfig?.email_template_id || null;
          const newTemplateId = step.template?.id ? parseInt(step.template.id, 10) : null;
          const nameChanged = step.name !== existingName;
          const descChanged = step.description !== existingDesc;
          const templateChanged = newTemplateId !== existingTemplateId;
          if (!nameChanged && !descChanged && !templateChanged) continue;
          try {
            const patchedConfig = {
              ...(existing.eventConfig || {}),
              name: step.name || existingName,
              description: step.description || existingDesc,
            };
            if (newTemplateId !== null) patchedConfig.email_template_id = newTemplateId;
            const evtResult = await updateCadenceEvent(String(effectiveCadenceId), String(step.id), getVersion(), {
              eventConfig: patchedConfig,
            });
            if (evtResult?.cadenceVersion) trackVersion(evtResult.cadenceVersion);
          } catch (err) {
            console.warn(`[cadences] Step ${step.id} content save failed:`, err.message);
          }
        }
      }

      // For active cadences, push sender details to all email steps via dedicated endpoint
      // (event content is otherwise locked on active cadences).
      const isActiveCadence = (existingCadence?.displayStatusLabel || '').toLowerCase() === 'active';
      if (isActiveCadence && senderEmailPrefix) {
        try {
          const senderResult = await updateCadenceSenderConfig(
            effectiveCadenceId,
            getVersion(),
            `${senderEmailPrefix}@${orgDomain}`,
            senderName || null,
            parseReplyToEmails(replyToEmail) || null
          );
          if (senderResult?.cadenceVersion) trackVersion(senderResult.cadenceVersion);
        } catch (err) {
          console.warn('[cadences] Sender config update failed:', err.message);
        }
      }

      setLastSavedAt(new Date());
      setIsDirty(false);
      EverTrue.Alert.success('Settings saved');
    } catch (err) {
      EverTrue.Alert.error(`Failed to save: ${formatSaveError(err)}`);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  /** Called from the blueprint modal with the user-chosen name. */
  const handleSaveAsShell = async (blueprintName, checkedOptions = {}) => {
    if (savingRef.current) return;
    const cadenceSaveName = cadenceName.trim() || blueprintName;
    if (!cadenceSaveName) {
      EverTrue.Alert.error(`Please enter a ${typeLabel.toLowerCase()} name before saving as a blueprint`);
      return;
    }
    // Validate automated email steps have subject/body before attempting save
    const incompleteEmails = getIncompleteEmailSteps(steps);
    if (incompleteEmails.length > 0) {
      EverTrue.Alert.error(
        'All automated email steps must have a subject line and body content before saving as a blueprint.'
      );
      return;
    }
    savingRef.current = true;
    setIsSaving(true);
    try {
      // Create the underlying cadence as a draft atomically, then save it as a blueprint
      const includeTags = checkedOptions.tags !== false;
      const result = await cadenceGqlCall(CREATE_CADENCE_WITH_CONFIG_MUTATION, {
        input: {
          cadence: {
            name: cadenceSaveName,
            cadenceType,
            status: 'draft',
            tags: includeTags ? campaignTags : [],
            sourceCadenceTemplateId: cadenceTemplate ? parseInt(cadenceTemplate, 10) : null,
            singleEnrollmentOnly,
            enrollmentLocked,
          },
          events: buildEventsWithRefIds(),
          branches: buildBranchesForConfig(),
          successMetrics: buildSuccessMetricsInput(),
          autoRemovalRules: buildAutoRemovalRulesInput(),
          sharing: buildSharingInput(),
          proposalIds: linkedProposals.map((p) => parseInt(p.value, 10)).filter((n) => !isNaN(n)),
        },
      });
      const createResult = result?.createCadenceWithConfig;
      if (!createResult?.success) {
        throw new Error(createResult?.message || 'Failed to create cadence');
      }
      const newCadenceId = createResult.cadence?.id;
      if (newCadenceId) {
        await saveCadenceAsTemplate(newCadenceId, { name: blueprintName });
        EverTrue.Alert.success(`Saved as cadence blueprint "${blueprintName}"`);
        EverTrue.Navigator('/cadences-v2?tab=shells', true);
      }
    } catch (err) {
      EverTrue.Alert.error(`Failed to save as blueprint: ${formatSaveError(err)}`);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  /** Check if name is still the default — if so, show prompt before proceeding. */
  const requireName = (action) => {
    if (
      cadenceName === defaultName ||
      cadenceName === 'Untitled Cadence' ||
      cadenceName === 'Untitled Prospect Plan' ||
      cadenceName === 'Untitled Blueprint' ||
      !cadenceName.trim()
    ) {
      setNamePromptValue(cadenceName);
      setPendingAction(() => action);
      setNamePromptOpen(true);
    } else {
      action(cadenceName);
    }
  };

  const handleUseForNewCadenceSubmit = async () => {
    const name = useNewFromTemplateName.trim();
    if (!name) return;
    setUseNewFromTemplateOpen(false);
    try {
      const result = await createCadenceFromTemplate(effectiveCadenceId, {
        name,
        status: 'draft',
        ...(cadenceType ? { cadenceType } : {}),
      });
      EverTrue.Alert.success('Cadence created from blueprint');
      if (result?.cadence?.id) {
        const resultType = result.cadence.cadenceType || cadenceType;
        const route =
          resultType === 'bulk_message'
            ? `/cadences-v2/bulk-message/${result.cadence.id}/edit`
            : `/cadences-v2/${result.cadence.id}`;
        EverTrue.Navigator(route, { trigger: true });
      }
    } catch (err) {
      EverTrue.Alert.error(`Could not create cadence: ${err.message}`);
    }
  };

  const handleToggleBlueprintStatus = async () => {
    if (!effectiveCadenceId || !existingCadence || isSaving) return;
    const isActive = existingCadence.displayStatusLabel?.toLowerCase() === 'active';
    const newStatus = isActive ? 'draft' : 'active';
    savingRef.current = true;
    setIsSaving(true);
    try {
      const result = await cadenceGqlCall(UPDATE_CADENCE_MUTATION, {
        id: String(effectiveCadenceId),
        version: getVersion(),
        input: { status: newStatus },
      });
      const updated = result?.updateCadence?.cadence;
      if (!updated) throw new Error(`Failed to ${isActive ? 'unpublish' : 'publish'} blueprint`);
      trackVersion(updated.version);
      EverTrue.Alert.success(isActive ? 'Blueprint unpublished.' : 'Blueprint published and available for use.');
    } catch (err) {
      EverTrue.Alert.error(`Failed to ${isActive ? 'unpublish' : 'publish'} blueprint: ${err.message}`);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleNamePromptConfirm = () => {
    const name = namePromptValue.trim() || defaultName;
    setCadenceName(name);
    setNamePromptOpen(false);
    const action = pendingAction;
    setPendingAction(null);
    if (action) action(name);
  };

  const handleNamePromptCancel = () => {
    setNamePromptOpen(false);
    setPendingAction(null);
  };

  const handleAddTag = () => {
    const existingLower = campaignTags.map((t) => t.toLowerCase());
    const newTags = tagInput
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter((t) => t && !existingLower.includes(t));
    if (newTags.length > 0) {
      setCampaignTags([...campaignTags, ...newTags]);
      newTags.forEach((t) => cadenceGqlCall(REGISTER_TAG_MUTATION, { name: t }).catch(() => {}));
    }
    setTagInput('');
  };

  const handleRemoveTag = (tag) => {
    setCampaignTags(campaignTags.filter((t) => t !== tag));
  };

  /** Fetch tag autocomplete suggestions from org-wide registry, debounced 300ms. */
  useEffect(() => {
    if (!tagInput.trim()) {
      setTagSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      cadenceGqlCall(ORG_TAGS_QUERY, { search: tagInput.trim() })
        .then((data) => {
          setTagSuggestions(data?.orgTags || []);
        })
        .catch(() => setTagSuggestions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [tagInput]);

  /** Add a single tag from the autocomplete suggestions (already registered). */
  const handleSelectTagSuggestion = (tag) => {
    const normalized = tag.toLowerCase();
    if (!campaignTags.map((t) => t.toLowerCase()).includes(normalized)) {
      setCampaignTags([...campaignTags, normalized]);
    }
    setTagInput('');
    setTagSuggestions([]);
  };

  const getRecipientCount = () => {
    const method = selectionMethod?.value;
    if (method === 'list' && selectedList?.value) {
      return selectedList.count || 0;
    }
    if (method === 'search' && selectedSavedSearch?.value) {
      return selectedSavedSearch.count || 0;
    }
    if (method === 'contacts') {
      if (selectedContacts.length > 0) return selectedContacts.length;
      if (selectedContacts.length > 0) return selectedContacts.length;
    }
    return 0;
  };

  /** Callback for the shared template picker modal. */
  const handleTemplateSelect = (template) => {
    const subject = template.templateSubject || '';
    const body = template.templateBody || template.dynamicTemplate || '';
    handleDrawerFieldChange('template', { id: template.id, name: template.templateName || 'Selected Template' });
    handleDrawerFieldChange('subjectOverride', subject);
    handleDrawerFieldChange('emailBody', body);
    handleDrawerFieldChange('templateOriginalSubject', subject);
    handleDrawerFieldChange('templateOriginalBody', body);
    setShowTemplateModal(false);
  };

  const handleTemplateUnlink = () => {
    handleDrawerFieldChange('template', null);
    handleDrawerFieldChange('templateOriginalSubject', null);
    handleDrawerFieldChange('templateOriginalBody', null);
  };

  /** Callback for the shared signature picker modal. */
  const handleSignatureSelect = (sig) => {
    handleDrawerFieldChange('signature', sig.id === 0 ? null : sig);
    setShowSignatureModal(false);
  };

  const handleDisconnectAudience = async (audienceId) => {
    try {
      const result = await deleteCadenceAudience(audienceId);
      if (result?.success) {
        refetchCadence();
      } else {
        EverTrue.Alert.error(result?.message || 'Failed to disconnect audience');
      }
    } catch (err) {
      EverTrue.Alert.error(`Failed to disconnect: ${err.message}`);
    }
  };

  const handleDisconnectAllManual = async (individualAudiences) => {
    // TODO(backend): replace with a bulk delete mutation to avoid N round-trips
    try {
      await Promise.all(individualAudiences.map((a) => deleteCadenceAudience(a.id)));
      refetchCadence();
      EverTrue.Alert.success('Removed all manually added contacts');
    } catch (err) {
      EverTrue.Alert.error(`Failed to remove contacts: ${err.message}`);
    }
  };

  /** Load steps from a shell/template cadence for prospect plans (deep copy). */
  const loadStepsFromShell = async (shellId) => {
    try {
      const { CADENCE_QUERY } = await import('../../utils/cadence-queries');
      const data = await cadenceGqlCall(CADENCE_QUERY, { id: String(shellId) });
      const shellCadence = data?.cadence;
      if (!shellCadence?.events?.length) return;

      const shellEdges = shellCadence.edges || [];
      const shellEventById = {};
      shellCadence.events.forEach((e) => {
        shellEventById[String(e.id)] = e;
      });

      const shellEventToStep = (e) => {
        const evtType = e.eventType.toLowerCase();
        const isBackendTask = evtType === 'task';
        const isLegacyManualEmail = evtType === 'email' && e.isAutomated === false;
        const isTask = isBackendTask || isLegacyManualEmail;
        const actionType = e.eventConfig?.action_type || 'send_email';
        const taskTypeId = actionType === 'send_email' ? 'email' : actionType;
        return {
          id: `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: isTask ? 'task' : evtType,
          taskType: isTask ? taskTypeId : undefined,
          name: e.eventConfig?.name || '',
          description: e.eventConfig?.description || '',
          subjectOverride: e.eventConfig?.subject || '',
          emailBody: e.eventConfig?.html_body || e.eventConfig?.text_body || '',
          attachments: e.eventConfig?.attachments || [],
          waitType: e.eventConfig?.wait_type || 'duration',
          waitDuration:
            e.eventConfig?.duration_minutes || e.eventConfig?.duration_days || e.eventConfig?.duration_hours || 1,
          waitUnit: e.eventConfig?.duration_minutes ? 'minutes' : e.eventConfig?.duration_hours ? 'hours' : 'days',
          waitUntil: e.eventConfig?.wait_until || '',
          conditionType: e.eventConfig?.condition_type || 'always_true',
          condition: restoreConditionFromConfig(e.eventConfig),
          template: e.eventConfig?.email_template_id
            ? { id: e.eventConfig.email_template_id, name: e.eventConfig.template_name || 'Template' }
            : null,
          signature: e.eventConfig?.signature_id
            ? { id: e.eventConfig.signature_id, signature_name: e.eventConfig.signature_name || 'Signature' }
            : null,
          skipBusinessDayCheck: e.eventConfig?.skip_business_day_check || false,
        };
      };

      // Follow edge chain from a starting event
      const shellFollowChain = (startId) => {
        const chain = [];
        let curId = startId;
        const visited = new Set();
        while (curId && !visited.has(curId)) {
          visited.add(curId);
          const evt = shellEventById[String(curId)];
          if (!evt || evt.eventType.toLowerCase() === 'end') break;
          chain.push(shellEventToStep(evt));
          const nextEdge = shellEdges.find(
            (ed) => String(ed.fromCadenceEventId) === String(curId) && ed.edgeType?.toLowerCase() === 'next'
          );
          curId = nextEdge ? nextEdge.toCadenceEventId : null;
        }
        return chain;
      };

      // Mark branch child events
      const shellBranchIds = new Set();
      const shellCollectBranch = (startId) => {
        let curId = startId;
        const visited = new Set();
        while (curId && !visited.has(curId)) {
          visited.add(curId);
          const evt = shellEventById[String(curId)];
          if (!evt || evt.eventType.toLowerCase() === 'end') break;
          shellBranchIds.add(String(curId));
          const nextEdge = shellEdges.find(
            (ed) => String(ed.fromCadenceEventId) === String(curId) && ed.edgeType?.toLowerCase() === 'next'
          );
          curId = nextEdge ? nextEdge.toCadenceEventId : null;
        }
      };

      shellCadence.events.forEach((e) => {
        if (e.eventType.toLowerCase() === 'condition') {
          const succEdge = shellEdges.find(
            (ed) => String(ed.fromCadenceEventId) === String(e.id) && ed.edgeType?.toLowerCase() === 'success'
          );
          const failEdge = shellEdges.find(
            (ed) => String(ed.fromCadenceEventId) === String(e.id) && ed.edgeType?.toLowerCase() === 'failure'
          );
          if (succEdge) shellCollectBranch(succEdge.toCadenceEventId);
          if (failEdge) shellCollectBranch(failEdge.toCadenceEventId);
        }
      });

      const userEvents = shellCadence.events
        .filter((e) => {
          const type = e.eventType.toLowerCase();
          return type !== 'start' && type !== 'end' && !shellBranchIds.has(String(e.id));
        })
        .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
        .map((e) => {
          const step = shellEventToStep(e);
          if (e.eventType.toLowerCase() === 'condition') {
            const succEdge = shellEdges.find(
              (ed) => String(ed.fromCadenceEventId) === String(e.id) && ed.edgeType?.toLowerCase() === 'success'
            );
            const failEdge = shellEdges.find(
              (ed) => String(ed.fromCadenceEventId) === String(e.id) && ed.edgeType?.toLowerCase() === 'failure'
            );
            step.branches = {
              true: succEdge ? shellFollowChain(succEdge.toCadenceEventId) : [],
              false: failEdge ? shellFollowChain(failEdge.toCadenceEventId) : [],
            };
          }
          return step;
        });
      setSteps(userEvents);

      // Load success metrics from blueprint
      const shellMetrics = shellCadence.successMetrics || [];
      if (shellMetrics.length > 0) {
        const m = shellMetrics[0];
        setSuccessMetric({
          id: crypto.randomUUID(),
          type: normalizeKey(m.metricType || ''),
          targetPercentage: m.targetPercentage ?? 0,
          isPrimary: true,
          config: {
            interactionType: m.metricConfig?.interaction_type || null,
            minAmount: m.metricConfig?.min_amount ? String(m.metricConfig.min_amount) : null,
            designation: m.metricConfig?.designation || null,
            teamId: m.metricConfig?.team_id ? String(m.metricConfig.team_id) : null,
            teamName: m.metricConfig?.team_name || null,
            stageId: m.metricConfig?.stage_id ? String(m.metricConfig.stage_id) : null,
            stageName: m.metricConfig?.stage_name || null,
            savedSearchId: m.metricConfig?.saved_search_tag_id || m.metricConfig?.saved_search_id || null,
            savedSearchName: m.metricConfig?.saved_search_name || null,
            savedSearchListId: m.metricConfig?.saved_search_list_id ?? null,
            threshold: m.metricConfig?.threshold || m.metricConfig?.threshold_value || null,
            assignmentStage: m.metricConfig?.assignment_stage || null,
          },
        });
      }

      // Load auto-removal rules from blueprint
      const rules = (shellCadence.autoRemovalRules || []).map((r) => {
        const type = normalizeKey(r.removalType || '');
        return {
          id: Date.now() + Math.random(),
          type,
          config: normalizeRemovalConfig(type, r.removalConfig),
        };
      });
      if (rules.length > 0) setRemovalRules(rules);

      // Load tags from blueprint
      if (shellCadence.tags?.length > 0) {
        setCampaignTags(shellCadence.tags);
      }

      // Pre-fill cadence name from blueprint
      if (shellCadence.name) {
        setCadenceName(shellCadence.name);
      }

      // Restore sender settings from the first email event with a from_email config.
      // Don't filter on isAutomated — manual email steps also carry from_email.
      const firstAutoEmail = shellCadence.events.find(
        (e) => e.eventType?.toLowerCase() === 'email' && e.eventConfig?.from_email
      );
      if (firstAutoEmail?.eventConfig) {
        const ec = firstAutoEmail.eventConfig;
        if (ec.from_name) setSenderName(ec.from_name);
        if (ec.from_email) {
          const atIdx = ec.from_email.indexOf('@');
          if (atIdx >= 0) {
            setSenderEmailPrefix(ec.from_email.slice(0, atIdx));
          } else {
            setSenderEmailPrefix(ec.from_email);
          }
        }
        const blueprintReplyTo =
          (Array.isArray(ec.reply_to_emails) ? ec.reply_to_emails.join(', ') : null) || ec.reply_to_email;
        if (blueprintReplyTo) setReplyToEmail(blueprintReplyTo);
      }

      // Restore linked proposals from blueprint
      if (shellCadence.linkedProposals?.length > 0) {
        const restored = shellCadence.linkedProposals.map((lp) => {
          const pid = String(lp.proposalId);
          const match = proposals.find((p) => p.value === pid);
          return { value: pid, label: match?.label || `Proposal #${pid}` };
        });
        setLinkedProposals(restored);
      }

      // Restore sharing settings from blueprint
      fetchCadenceSharingSettings(shellId)
        .then((sharingSettings) => {
          if (sharingSettings?.length > 0) {
            const userShares = sharingSettings
              .filter((s) => s.userId !== 0)
              .map((s) => ({
                userId: s.userId,
                permission: s.canEdit ? 'edit' : 'view',
                label: `User ${s.userId}`,
              }));
            if (userShares.length > 0) {
              setSharedUsers(userShares);
              fetchUsersById(userShares.map((u) => u.userId))
                .then((users) => {
                  const nameMap = {};
                  (users || []).forEach((u) => {
                    nameMap[u.id] = u.name || u.email || `User ${u.id}`;
                  });
                  setSharedUsers(userShares.map((u) => ({ ...u, label: nameMap[u.userId] || u.label })));
                })
                .catch((e) => console.warn('[cadences] background fetch failed:', e.message));
            }
          }
        })
        .catch((e) => console.warn('[cadences] background fetch failed:', e.message));

      EverTrue.Alert.success(`Loaded ${userEvents.length} step(s) from blueprint — content is editable`);
    } catch (err) {
      console.warn('[cadences] Failed to load blueprint steps:', err.message);
      EverTrue.Alert.error('Failed to load steps from blueprint. Please try again.');
    }
  };

  const handleStepSelect = (index) => {
    setSelectedBranch(null);
    setSelectedStepIndex(selectedStepIndex === index ? null : index);
    setDrawerTab('details');
  };

  const handleBranchSelect = (branch) => {
    if (branch) {
      setSelectedStepIndex(null);
    }
    setSelectedBranch(branch);
    setDrawerTab('details');
  };

  const handleDrawerClose = () => {
    setSelectedStepIndex(null);
    setSelectedBranch(null);
  };

  const handleDrawerFieldChange = (field, value) => {
    if (selectedBranch !== null) {
      const { stepIndex, branchKey, branchStepIndex } = selectedBranch;
      setSteps((prev) => {
        const newSteps = [...prev];
        const branchArray = [...(newSteps[stepIndex].branches[branchKey] || [])];
        branchArray[branchStepIndex] = { ...branchArray[branchStepIndex], [field]: value };
        newSteps[stepIndex] = {
          ...newSteps[stepIndex],
          branches: {
            ...newSteps[stepIndex].branches,
            [branchKey]: branchArray,
          },
        };
        return newSteps;
      });
      return;
    }
    if (selectedStepIndex === null) return;
    setSteps((prev) => {
      const newSteps = [...prev];
      newSteps[selectedStepIndex] = { ...newSteps[selectedStepIndex], [field]: value };
      return newSteps;
    });
  };

  /**
   * Renders the horizontal stepper.
   */
  const stepperSteps = [
    { id: 1, label: `Configure ${typeLabel}` },
    { id: 2, label: `${typeLabel} Steps` },
    { id: 3, label: isBlueprint ? 'Review & Save' : 'Review & Publish' },
  ];

  const renderStepper = () => (
    <div className="create-cadence-stepper">
      {stepperSteps.map((step, i) => (
        <div key={step.id} className="create-cadence-stepper--item">
          {i > 0 && (
            <div className={`create-cadence-stepper--line ${currentStep > step.id - 1 ? 'is-completed' : ''}`} />
          )}
          <button
            type="button"
            className={`create-cadence-stepper--circle ${currentStep === step.id ? 'is-active' : ''} ${currentStep > step.id ? 'is-completed' : ''}`}
            onClick={() => setCurrentStep(step.id)}
          >
            {currentStep > step.id ? <Icon icon="check" /> : step.id}
          </button>
          <span className={`create-cadence-stepper--label ${currentStep === step.id ? 'is-active' : ''}`}>
            {step.label}
          </span>
        </div>
      ))}
    </div>
  );

  if (cadenceId && existingLoading) {
    return (
      <div className="create-cadence-page">
        <div className="create-cadence-page--loading">Loading cadence data...</div>
      </div>
    );
  }

  // Save button label rules:
  // 1. Never saved → "Save as Draft"
  // 2. Saved as draft → "Save"
  // 3. Active/live → "Save as Draft" (demotes back to draft)
  // Blueprint variants follow the same pattern with "Blueprint" instead of "Draft"
  const getSaveLabel = () => {
    if (isSaving) return 'Saving...';
    if (isBlueprint) {
      if (!effectiveCadenceId) return 'Save Blueprint';
      return 'Save';
    }
    if (!effectiveCadenceId) return 'Save as Draft';
    if (existingCadence?.displayStatusLabel?.toLowerCase() === 'active') return 'Save as Draft';
    return 'Save';
  };

  return (
    <div className="create-cadence-page">
      {/* Readonly Banner */}
      {readonly && (
        <div className="create-cadence-page--readonly-banner">
          <Icon icon="info" />
          <span>Viewing cadence setup in read-only mode</span>
        </div>
      )}
      {!readonly && !isBlueprint && existingCadence?.displayStatusLabel?.toLowerCase() === 'active' && (
        <div className="create-cadence-page--readonly-banner">
          <Icon icon="info" />
          <span>
            This cadence is live. The cadence flow steps cannot be modified, but you can update settings, metrics, and
            audience below.
          </span>
        </div>
      )}

      {/* Header */}
      <div className="create-cadence-page--header">
        <div className="create-cadence-page--header-left">
          <div className="create-cadence-page--name-area">
            {!readonly && isEditingName ? (
              <input
                type="text"
                className="create-cadence-page--name-input"
                value={cadenceName}
                onChange={(e) => setCadenceName(e.target.value)}
                onBlur={() => setIsEditingName(false)}
                onKeyDown={(e) => e.key === 'Enter' && setIsEditingName(false)}
                autoFocus
              />
            ) : (
              <button
                type="button"
                className="create-cadence-page--name-display"
                onClick={() => !readonly && setIsEditingName(true)}
              >
                <h1 className="create-cadence-page--title">{cadenceName || defaultName}</h1>
                {!readonly && <Icon icon="edit" className="create-cadence-page--name-edit-icon" />}
              </button>
            )}
            <span className="create-cadence-page--last-saved">
              {readonly
                ? 'Read-only view'
                : !effectiveCadenceId
                  ? 'Unsaved'
                  : existingCadence?.displayStatusLabel || existingCadence?.displayStatus || 'Draft'}
              {!readonly && isDirty && effectiveCadenceId && (
                <span className="create-cadence-page--unsaved-indicator"> (unsaved changes)</span>
              )}
            </span>
          </div>
        </div>
        <div className="create-cadence-page--header-actions">
          {readonly ? (
            <Button type="secondary" onClick={navigateAway}>
              <Icon icon="chevron-left" /> Back to Detail
            </Button>
          ) : isBlueprint ? (
            <>
              {currentStep > 1 && (
                <Button type="secondary" onClick={handlePrevStep}>
                  <Icon icon="chevron-left" /> Previous
                </Button>
              )}
              {currentStep < 3 ? (
                <Button type="action" onClick={handleNextStep}>
                  Next Step
                </Button>
              ) : effectiveCadenceId && existingCadence?.isTemplate ? (
                <>
                  <Button type="secondary" onClick={() => requireName(handleSaveDraft)} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                  {existingCadence.canDelete && (
                    <Button type="secondary" onClick={handleToggleBlueprintStatus} disabled={isSaving}>
                      {existingCadence.displayStatusLabel?.toLowerCase() === 'active'
                        ? 'Unpublish'
                        : 'Publish Blueprint'}
                    </Button>
                  )}
                  <Button
                    type="action"
                    disabled={isSaving}
                    onClick={() => {
                      setUseNewFromTemplateName('');
                      setUseNewFromTemplateOpen(true);
                    }}
                  >
                    <Icon icon="add" />{' '}
                    {cadenceType === 'bulk_message' ? 'Use for New Bulk Message' : 'Use for New Cadence'}
                  </Button>
                </>
              ) : (
                <Button type="action" onClick={() => requireName(handleSaveDraft)} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Blueprint'}
                </Button>
              )}
            </>
          ) : ['active', 'paused'].includes(existingCadence?.displayStatusLabel?.toLowerCase()) ? (
            <>
              <Button type="secondary" onClick={() => EverTrue.Navigator(`/cadences-v2/${effectiveCadenceId}`, true)}>
                <Icon icon="chevron-left" /> Back to Detail
              </Button>
              <Button type="action" onClick={() => requireName(handleSaveSettings)} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Settings'}
              </Button>
            </>
          ) : (
            <>
              {currentStep > 1 && (
                <Button type="secondary" onClick={handlePrevStep}>
                  <Icon icon="chevron-left" /> Previous
                </Button>
              )}
              <Button
                type="secondary"
                onClick={() => requireName(handleSaveDraft)}
                className="create-cadence-page--save-draft-btn"
                disabled={isSaving}
              >
                {getSaveLabel()}
              </Button>
              {currentStep < 3 ? (
                <Button type="action" onClick={handleNextStep}>
                  Next Step
                </Button>
              ) : !existingCadence || existingCadence.canDelete ? (
                <Button
                  type="action"
                  onClick={() => requireName(() => setShowPublishConfirm(true))}
                  disabled={isSaving}
                >
                  {isSaving ? 'Publishing...' : 'Publish'}
                </Button>
              ) : null}
            </>
          )}
          <button type="button" className="create-cadence-page--close-btn" aria-label="Close" onClick={handleClose}>
            <Icon icon="close" />
          </button>
        </div>
      </div>

      {/* Stepper */}
      {renderStepper()}

      {/* Step Content */}
      <div className="create-cadence-page--body">
        {currentStep === 1 && (
          <CadenceSetupStep
            isDebugMode={isDebugMode}
            isBlueprint={isBlueprint}
            isProspectPlan={isProspectPlan}
            typeLabel={typeLabel}
            readonly={readonly}
            canDelete={!existingCadence || !!existingCadence.canDelete}
            orgDomain={orgDomain}
            successMetric={successMetric}
            setSuccessMetric={setSuccessMetric}
            additionalMetrics={additionalMetrics}
            setAdditionalMetrics={setAdditionalMetrics}
            interactionTypes={interactionTypes}
            savedSearches={savedSearches}
            portfolioTeams={portfolioTeams}
            isRecipientsOpen={isRecipientsOpen}
            setIsRecipientsOpen={setIsRecipientsOpen}
            selectionMethod={selectionMethod}
            setSelectionMethod={setSelectionMethod}
            selectedList={selectedList}
            setSelectedList={setSelectedList}
            selectedSavedSearch={selectedSavedSearch}
            setSelectedSavedSearch={setSelectedSavedSearch}
            selectedContacts={selectedContacts}
            setSelectedContacts={setSelectedContacts}
            contactLists={contactLists}
            existingAudiences={existingCadence?.audiences}
            audiencesLoaded={!!effectiveCadenceId && !existingLoading}
            onDisconnectAudience={handleDisconnectAudience}
            onDisconnectAllManual={handleDisconnectAllManual}
            getRecipientCount={getRecipientCount}
            isAdvancedOpen={isAdvancedOpen}
            setIsAdvancedOpen={setIsAdvancedOpen}
            isExitConditionsOpen={isExitConditionsOpen}
            setIsExitConditionsOpen={setIsExitConditionsOpen}
            removalRules={removalRules}
            setRemovalRules={setRemovalRules}
            cadenceOptions={cadenceOptions}
            isSenderDetailsOpen={isSenderDetailsOpen}
            setIsSenderDetailsOpen={setIsSenderDetailsOpen}
            senderEmailPrefix={senderEmailPrefix}
            setSenderEmailPrefix={setSenderEmailPrefix}
            senderName={senderName}
            setSenderName={setSenderName}
            replyToEmail={replyToEmail}
            setReplyToEmail={setReplyToEmail}
            isSharingOpen={isSharingOpen}
            setIsSharingOpen={setIsSharingOpen}
            sharedUsers={sharedUsers}
            setSharedUsers={setSharedUsers}
            pendingShareUsers={pendingShareUsers}
            setPendingShareUsers={setPendingShareUsers}
            pendingSharePermission={pendingSharePermission}
            setPendingSharePermission={setPendingSharePermission}
            isProposalsOpen={isProposalsOpen}
            setIsProposalsOpen={setIsProposalsOpen}
            linkedProposals={linkedProposals}
            setLinkedProposals={setLinkedProposals}
            proposals={proposals}
            onProposalSearch={setProposalSearch}
            isBlueprintOpen={isBlueprintOpen}
            setIsBlueprintOpen={setIsBlueprintOpen}
            cadenceTemplate={cadenceTemplate}
            setCadenceTemplate={setCadenceTemplate}
            templateSearch={templateSearch}
            setTemplateSearch={setTemplateSearch}
            templateDropdownOpen={templateDropdownOpen}
            setTemplateDropdownOpen={setTemplateDropdownOpen}
            cadenceShells={cadenceShells}
            isTagsOpen={isTagsOpen}
            setIsTagsOpen={setIsTagsOpen}
            campaignTags={campaignTags}
            tagInput={tagInput}
            setTagInput={setTagInput}
            tagSuggestions={tagSuggestions}
            handleAddTag={handleAddTag}
            handleRemoveTag={handleRemoveTag}
            handleSelectTagSuggestion={handleSelectTagSuggestion}
            isEnrollmentLimitOpen={isEnrollmentLimitOpen}
            setIsEnrollmentLimitOpen={setIsEnrollmentLimitOpen}
            singleEnrollmentOnly={singleEnrollmentOnly}
            setSingleEnrollmentOnly={setSingleEnrollmentOnly}
            enrollmentLocked={enrollmentLocked}
            setEnrollmentLocked={setEnrollmentLocked}
          />
        )}
        {currentStep === 2 && (
          <CadenceBuilderStep
            steps={steps}
            setSteps={setSteps}
            selectedStep={selectedStep}
            selectedStepIndex={selectedStepIndex}
            selectedBranch={selectedBranch}
            drawerTab={drawerTab}
            setDrawerTab={setDrawerTab}
            typeLabel={typeLabel}
            isProspectPlan={isProspectPlan}
            isDebugMode={isDebugMode}
            stepTypeLabels={STEP_TYPE_LABELS}
            onStepSelect={handleStepSelect}
            onBranchSelect={handleBranchSelect}
            onDrawerClose={handleDrawerClose}
            onDrawerFieldChange={handleDrawerFieldChange}
            outreachTemplates={outreachTemplates}
            signatures={signatures || []}
            onTemplateSelect={handleTemplateSelect}
            onSignatureChange={(sig) => handleDrawerFieldChange('signature', sig)}
            onTemplateUnlink={handleTemplateUnlink}
            domainStatus={domainStatus}
            isOwner={isOwner}
            flowLocked={flowLocked}
          />
        )}
        {currentStep === 3 &&
          !isBlueprint &&
          (domainStatus === 'not_configured' || domainStatus === 'pending') &&
          steps.some(
            (s) =>
              s.type === 'email' ||
              (s.branches?.true || []).some((b) => b.type === 'email') ||
              (s.branches?.false || []).some((b) => b.type === 'email')
          ) && (
            <div className="create-cadence-review--domain-blocked">
              <div className="create-cadence-review--domain-blocked-title">
                <Icon icon="warning" />
                <strong>Publishing Blocked — Domain Not Verified</strong>
              </div>
              <p className="create-cadence-review--domain-blocked-body">
                {domainStatus === 'not_configured' ? (
                  <>
                    Your organization hasn&apos;t configured a custom sending domain. This is separate from your
                    personal email outreach account — it&apos;s an org-wide DNS setting that only an EverTrue Owner can
                    configure on the{' '}
                    {isOwner ? (
                      <a href="/settings/org_settings">organization settings page</a>
                    ) : (
                      'organization settings page'
                    )}
                    .
                  </>
                ) : (
                  <>
                    Your organization&apos;s domain verification is in progress (up to 48 hours). This cadence cannot be
                    activated until verification is complete.
                    {isOwner ? (
                      <>
                        {' '}
                        <a href="/settings/org_settings">Check status in Org Settings →</a>
                      </>
                    ) : (
                      ' Check with your organization Owner for status updates.'
                    )}
                  </>
                )}
              </p>
            </div>
          )}
        {currentStep === 3 && (
          <CadenceReviewStep
            cadenceName={cadenceName}
            typeLabel={typeLabel}
            isBlueprint={isBlueprint}
            isProspectPlan={isProspectPlan}
            steps={steps}
            successMetric={successMetric}
            selectionMethod={selectionMethod}
            selectedList={selectedList}
            selectedSavedSearch={selectedSavedSearch}
            selectedContacts={selectedContacts}
            getRecipientCount={getRecipientCount}
            sharedUsers={sharedUsers}
            campaignTags={campaignTags}
            removalRules={removalRules}
            linkedProposals={linkedProposals}
          />
        )}
      </div>

      {/* Save as Blueprint Modal */}
      <DuplicateCadenceModal
        isOpen={showBlueprintModal}
        onClose={() => setShowBlueprintModal(false)}
        onDuplicate={handleSaveAsShell}
        entityName={cadenceName || 'Untitled'}
        entityType="blueprint"
      />

      {/* Email Templates Modal */}
      <TemplatePickerModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        templates={outreachTemplates || []}
        onSelect={handleTemplateSelect}
        currentTemplateId={selectedStep?.template?.id}
      />

      {/* Email Signature Modal */}
      <SignaturePickerModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        signatures={signatures || []}
        onSelect={handleSignatureSelect}
        currentSignatureId={selectedStep?.signature?.id}
      />

      {/* Name Prompt Modal — shown when saving with a default "Untitled" name */}
      <Modal isOpen={namePromptOpen} closeModal={handleNamePromptCancel}>
        <ModalHeader title={`Name Your ${typeLabel}`} closeModal={handleNamePromptCancel} />
        <ModalBody>
          <div className="cadence-name-prompt">
            <p className="cadence-name-prompt--text">
              Give your {typeLabel.toLowerCase()} a name, or keep the default and save.
            </p>
            <input
              type="text"
              className="cadence-name-prompt--input"
              value={namePromptValue}
              onChange={(e) => setNamePromptValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNamePromptConfirm();
                }
              }}
              placeholder={`Enter ${typeLabel.toLowerCase()} name...`}
              autoFocus
            />
            <div className="cadence-name-prompt--actions">
              <Button type="secondary" onClick={handleNamePromptCancel}>
                Cancel
              </Button>
              <Button type="action" onClick={handleNamePromptConfirm}>
                Save
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={useNewFromTemplateOpen} closeModal={() => setUseNewFromTemplateOpen(false)}>
        <ModalHeader title="Name Your New Cadence" closeModal={() => setUseNewFromTemplateOpen(false)} />
        <ModalBody>
          <div className="cadence-name-prompt">
            <input
              type="text"
              className="cadence-name-prompt--input"
              value={useNewFromTemplateName}
              onChange={(e) => setUseNewFromTemplateName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleUseForNewCadenceSubmit();
                }
              }}
              placeholder="Enter cadence name..."
              autoFocus
            />
            <div className="cadence-name-prompt--actions">
              <Button type="secondary" onClick={() => setUseNewFromTemplateOpen(false)}>
                Cancel
              </Button>
              <Button type="action" onClick={handleUseForNewCadenceSubmit}>
                Create
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Publish Confirmation Modal */}
      <Modal isOpen={showPublishConfirm} closeModal={() => setShowPublishConfirm(false)}>
        <ModalHeader title="Publish Cadence?" closeModal={() => setShowPublishConfirm(false)} />
        <ModalBody>
          <div className="cadence-leave-confirm">
            <p>
              This will activate the cadence and begin sending messages to enrolled contacts. Once live, steps will run
              automatically on their scheduled intervals.
            </p>
            <div className="cadence-leave-confirm--actions">
              <Button type="secondary" onClick={() => setShowPublishConfirm(false)}>
                Cancel
              </Button>
              <Button
                type="action"
                onClick={() => {
                  setShowPublishConfirm(false);
                  handlePublish(cadenceName);
                }}
                disabled={isSaving}
              >
                Publish Cadence
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Leave Confirmation Modal */}
      <Modal isOpen={showLeaveConfirm} closeModal={() => setShowLeaveConfirm(false)}>
        <ModalHeader title="Unsaved Changes" closeModal={() => setShowLeaveConfirm(false)} />
        <ModalBody>
          <div className="cadence-leave-confirm">
            <p>Are you sure you want to leave without saving your changes?</p>
            <div className="cadence-leave-confirm--actions">
              <Button type="secondary" onClick={() => setShowLeaveConfirm(false)}>
                Stay
              </Button>
              <Button type="destroy" onClick={navigateAway}>
                Leave
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

CreateCadencePage.propTypes = {
  cadenceType: PropTypes.oneOf(['cadence', 'prospect_plan', 'bulk_message', 'blueprint']),
  cadenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shellId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  readonly: PropTypes.bool,
};

export default CreateCadencePage;
